// THE STANDING QUEUE (DESIGN §7.10) — where "AI proposes, humans commit" is actually performed.
//
// The page used to be a scroll of 96 equal cards. Every claim wore the same dress, so the one
// that duplicated REQ-021 looked exactly like the one that was new ground, and the operator's
// only defence against holding a duplicate was remembering the register. It did not hold. On
// 3 August #164 and #171 were filed 102 seconds apart with the same statement, both were held,
// and REQ-017 was created and deleted fifteen minutes later — a register reference burned for
// good, because references are never reused.
//
// So the queue is now TWO PANES: a rail of every claim, one line each with its worst flag, and a
// STAND where the selected claim is shown at full dress — what would enter the record, what the
// record ALREADY says, the linter in full, the evidence with the age of the document behind it,
// and a write preview that names the ref the click will mint. Four zones in a fixed order:
// the queue, the session ledger, the runs.
//
// The rail carries TWO TABS — UNTREATED (claims still standing) and TREATED (held, amended,
// refused, this session's included), each with its count. The tab is chosen once, when the
// first read lands — UNTREATED if anything stands, TREATED otherwise — and after that it moves
// only by the operator's own hand (a click, their last disposition, an undo): a poll that files
// a new claim never flips the rail or swaps the stand under them. The old
// Unheld / Held / Refused / All chips are gone — two filters over one list was one too many.
// The rail is also exactly the height of the stand beside it and scrolls inside the sheet,
// instead of stopping at 76vh with the stand running on below it.
//
// THE COLLISION LANE is the element the research asked for. `GET /api/proposals/context` runs
// pg_trgm over the register per pending claim; the twins check runs the SAME similarity
// (src/shared/similarity.ts) pairwise inside one run. Both only ever OFFER a refusal — the
// trigram catches lexical near-twins and misses semantic ones, so it is a filter that never
// fires falsely, not a detector. And when the comparison does not answer, the lane says the
// claim is UNCHECKED and bulk hold goes away: a missing check must never render as a passed
// check (B8).
//
// NOTHING was retired. Accept, accept-with-edits, accept-and-discuss, reject-with-reason,
// revert, bulk accept, the span-in-context sheet, the two rail tabs and j/k/a/e/r all survive —
// the two modals moved INTO the sheet (§4.3, §4.5), which is where the operator's design law
// says a control belongs.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useEngagement } from '../context/EngagementContext.js';
import { Tx } from '../components/Tx.js';
import { useToast } from '../components/Toast.js';
import ConfirmModal from '../components/ConfirmModal.js';
import EmptyState, { ProblemState } from '../components/EmptyState.js';
import { usePoll, POLL_MS } from '../hooks/usePoll.js';
import { ROUTES, useDeepLink, useOpenRoute, useRouteParams } from '../routes.js';
import { documentsApi, type SpanResult } from '../api/documents.js';
import { registerApi, type RegisterRow } from '../api/register.js';
import { agentApi, type Proposal, type ProposalEvidence, type RunHealth } from '../api/agent.js';
import {
  proposalsApi, PROPOSAL_REJECT_REASONS, REJECT_REASON_LABEL,
  type Neighbour, type ProposalsContext, type QueueRunStats,
} from '../api/proposals.js';
import { cutRun, type RailCell, type RailTone } from '../../shared/rail.js';
import { REQUIREMENT_STATUS_LABEL } from '../../shared/requirementStatus.js';
import { amendedFields, amendmentRef, isAmendmentKind, proposalSubject } from '../../shared/proposalSubject.js';
import { SIM_HIT, SIM_NEAR, SIM_FLOOR, trigramSimilarity } from '../../shared/similarity.js';
import { accountHead, parseClosingAccount } from '../../shared/closingAccount.js';
import type { Vars } from '../../shared/i18n.js';
import { useLocale, useT } from '../i18n/index.js';

import './ProposalsPage.de.js';
import './ProposalsPage.fr.js';

/** The translator pair the plain helper functions below receive from a component. */
type TFn = (key: string, vars?: Vars) => string;
type TpFn = (n: number, one: string, other: string, vars?: Vars) => string;

// ───────────────────────── vocabulary ─────────────────────────

/**
 * The queue's own nouns. The page says UNHELD / HOLD / HELD rather than pending / accept /
 * accepted, because the claim marks it has always drawn (◇ open, ▪ closed) mean exactly that:
 * a claim nobody has stood behind, and a claim an operator now stands behind.
 */
const KIND_NOUN: Record<string, string> = {
  create_requirement: 'requirement',
  update_requirement: 'revision',
  merge: 'merge',
  conflict: 'conflict',
  create_deliverable: 'deliverable',
  create_deliverable_item: 'content item',
  create_ac: 'criterion',
  create_test_case: 'test case',
  open_question: 'question',
  // An amendment names the KIND of thing it changes, so the rail can be read at a glance —
  // "deliverable amendment · DEL-04" rather than the raw constraint value the code happens to
  // use. update_requirement is already 'revision' above; these follow that spirit.
  update_deliverable: 'deliverable amendment',
  update_deliverable_item: 'content item amendment',
  update_acceptance_criterion: 'criterion amendment',
  update_test_case: 'test case amendment',
  provisional: 'provisional mark',
};

const TYPES = ['functional', 'non_functional', 'constraint', 'assumption', 'out_of_scope'];
const PRIORITIES = ['must', 'should', 'could', 'wont'];

// ── the rail's order and its attribute column ─────────────────────────────────
type SortKey = 'run' | 'type' | 'priority' | 'category' | 'kind';
const SORT_KEYS: Array<{ key: SortKey; label: string }> = [
  { key: 'run', label: 'run' }, { key: 'type', label: 'type' }, { key: 'priority', label: 'weight' },
  { key: 'category', label: 'category' }, { key: 'kind', label: 'kind' },
];
const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
/** The three tags a requirement claim carries — type, weight, category; other kinds carry what they have. */
const attrOf = (p: Proposal) => ({ type: str(p.payload.type), priority: str(p.payload.priority), category: str(p.payload.category) });
const typeWord = (t: string): string => (t === 'non_functional' ? 'non-functional' : t === 'out_of_scope' ? 'exclusion' : t.replace(/_/g, ' '));
const weightWord = (w: string): string => (w === 'wont' ? "won't" : w);
const rank = (list: string[], v: string): number => { const i = list.indexOf(v); return i < 0 ? list.length : i; };
const KIND_ORDER = Object.keys(KIND_NOUN);
/** Run order (newest run first, filing order inside it) is the default, and the tiebreak of every other key. */
function sortRows(rows: Proposal[], key: SortKey): Proposal[] {
  if (key === 'run') return rows;
  const byRun = (a: Proposal, b: Proposal) => (b.run_id ?? -1) - (a.run_id ?? -1) || a.id - b.id;
  const cmp: (a: Proposal, b: Proposal) => number =
    key === 'type' ? (a, b) => rank(TYPES, attrOf(a).type) - rank(TYPES, attrOf(b).type)
      : key === 'priority' ? (a, b) => rank(PRIORITIES, attrOf(a).priority) - rank(PRIORITIES, attrOf(b).priority)
        : key === 'category' ? (a, b) => { const x = attrOf(a).category; const y = attrOf(b).category; return (x ? 0 : 1) - (y ? 0 : 1) || x.localeCompare(y); }
          : (a, b) => rank(KIND_ORDER, a.kind) - rank(KIND_ORDER, b.kind);
  return [...rows].sort((a, b) => cmp(a, b) || byRun(a, b));
}
/** What a rail group header is keyed on, and says, under each sort. */
function groupOf(p: Proposal, key: SortKey, t: TFn): { id: string; label: string } {
  if (key === 'run') return { id: String(p.run_id ?? 'none'), label: p.run_id === null ? t('unattributed') : t('run {id}', { id: p.run_id }) };
  const a = attrOf(p);
  if (key === 'type') return { id: a.type || '—', label: a.type ? t(typeWord(a.type)) : t('no type') };
  if (key === 'priority') return { id: a.priority || '—', label: a.priority ? t(weightWord(a.priority)) : t('no weight') };
  if (key === 'category') return { id: a.category || '—', label: a.category || t('uncategorised') };
  return { id: p.kind, label: t(KIND_NOUN[p.kind] ?? p.kind) };
}

/** Kinds whose accept can also file an operator question on the client-call agenda. */
const DISCUSS_KINDS = ['create_requirement', 'update_requirement', 'create_ac'];

/** Kinds whose subject is compared against the register at large. */
const TRIGRAM_KINDS = new Set(['create_requirement', 'update_requirement', 'merge', 'create_deliverable']);

/**
 * Kinds whose hold can be reverted with a button — the accept CREATED a row, and revert deletes it
 * (refused by the server if something already depends on it). update / merge / conflict rewrote
 * existing history and are walked back through the requirement's revisions, never a button; this
 * set mirrors the server's ENTITY map in routes/agent.ts (/proposals/:id/revert).
 */
const REVERTIBLE_KINDS = new Set(['create_requirement', 'create_deliverable', 'create_ac', 'create_test_case']);

/** The rail's two tabs. `null` = automatic: UNTREATED while anything stands, TREATED once nothing does. */
type RailTab = 'untreated' | 'held' | 'treated';
/** Tab labels — 'held' already means «taken over» in the queue vocabulary, so the thread tab has its own key. */
const TAB_LABEL: Record<RailTab, string> = { untreated: 'untreated', held: 'in thread', treated: 'treated' };

function subjectOf(p: Proposal): string {
  return proposalSubject(p.kind, p.payload as Record<string, unknown>);
}

/**
 * The page's ONE word for a disposition, whoever made it and whenever. The server's statuses are
 * accepted / accepted_with_edits / rejected; this page has always said held / amended / refused.
 * A row treated in an earlier session must not read differently from one treated a minute ago —
 * "accepted" beside "held" on the same rail was two vocabularies for one fact.
 */
const DISPOSITION_WORD: Record<string, string> = {
  accepted: 'held',
  accepted_with_edits: 'amended',
  rejected: 'refused',
};
function dispositionWord(status: string, d: Disposition | null): string {
  if (d) return d.verdict;
  return DISPOSITION_WORD[status] ?? status.replace(/_/g, ' ');
}

// ───────────────────────── word diff ─────────────────────────

type DiffToken = { text: string; kind: 'same' | 'add' | 'del' };

/** Small LCS word diff — enough to make "what changed" readable at a glance. */
function wordDiff(before: string, after: string): DiffToken[] {
  const a = (before ?? '').split(/(\s+)/).filter(Boolean);
  const b = (after ?? '').split(/(\s+)/).filter(Boolean);
  const table: number[][] = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));
  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      table[i][j] = a[i] === b[j] ? table[i + 1][j + 1] + 1 : Math.max(table[i + 1][j], table[i][j + 1]);
    }
  }
  const out: DiffToken[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) { out.push({ text: a[i], kind: 'same' }); i += 1; j += 1; }
    else if (table[i + 1][j] >= table[i][j + 1]) { out.push({ text: a[i], kind: 'del' }); i += 1; }
    else { out.push({ text: b[j], kind: 'add' }); j += 1; }
  }
  while (i < a.length) { out.push({ text: a[i], kind: 'del' }); i += 1; }
  while (j < b.length) { out.push({ text: b[j], kind: 'add' }); j += 1; }
  return out;
}

function DiffLine({ before, after }: { before: string; after: string }) {
  const tokens = useMemo(() => wordDiff(before, after), [before, after]);
  return (
    <div className="pq-dif">
      {tokens.map((t, i) =>
        t.kind === 'same' ? <span key={i}>{t.text}</span>
          : t.kind === 'del' ? <del key={i}>{t.text}</del>
            : <ins key={i}>{t.text}</ins>,
      )}
    </div>
  );
}

// ───────────────────────── flags ─────────────────────────

/**
 * Every flag is COMPUTED, so none of them carries a claim mark (LAW 6). They are emitted in
 * severity order and the queue rail shows only the FIRST — one flag per row, or the rail becomes
 * the "count the 3s" table that makes the operator do the arithmetic the page exists to do.
 */
interface Flag { key: string; tone: 'attend' | 'broken' | 'mute'; short: string; long: string }

interface Twin { id: number; sim: number }

function flagsFor(
  p: Proposal,
  neighbours: Neighbour[] | null,
  twins: Twin[],
  checked: boolean,
  tr: { t: TFn; tp: TpFn },
): Flag[] {
  if (!checked && TRIGRAM_KINDS.has(p.kind)) {
    return [{ key: 'unknown', tone: 'mute', short: '?', long: tr.t('register comparison unavailable') }];
  }
  const out: Flag[] = [];
  const top = neighbours?.[0];
  if (twins.length) {
    out.push({ key: 'twin', tone: 'attend', short: tr.t('twin'), long: tr.t('twin of #{id} in this same run', { id: twins[0].id }) });
  }
  if (top && top.sim >= SIM_HIT) {
    out.push({ key: 'collide', tone: 'attend', short: tr.t('collides'), long: tr.t('collides with {ref}', { ref: top.ref }) });
  }
  const lint = p.payload.lint;
  if (lint && !lint.pass) out.push({ key: 'lint', tone: 'broken', short: tr.t('lint'), long: tr.t('lint error') });
  if (!p.evidence.length && p.kind !== 'create_deliverable' && p.kind !== 'create_ac') {
    out.push({ key: 'noev', tone: 'attend', short: tr.t('no ev'), long: tr.t('no evidence') });
  }
  if (lint && lint.pass && lint.flags.length) {
    out.push({ key: 'lintw', tone: 'mute', short: tr.t('warn'), long: tr.tp(lint.flags.length, '{n} lint warning', '{n} lint warnings') });
  }
  const conf = p.payload.confidence;
  if (typeof conf === 'number' && conf < 0.8) {
    out.push({ key: 'conf', tone: 'mute', short: tr.t('low conf'), long: tr.t('agent confidence {n}', { n: conf.toFixed(2) }) });
  }
  if (top && top.sim >= SIM_NEAR && top.sim < SIM_HIT) {
    out.push({ key: 'near', tone: 'mute', short: tr.t('near'), long: tr.t('nearest {ref} at {sim}', { ref: top.ref, sim: top.sim.toFixed(2) }) });
  }
  return out;
}

// ───────────────────────── the span-in-context sheet ─────────────────────────

function SpanSheet({
  evidence, docMeta, onClose,
}: {
  evidence: ProposalEvidence;
  docMeta?: { original_filename: string; effective_date: string | null };
  onClose: () => void;
}) {
  const [span, setSpan] = useState<SpanResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const t = useT();
  const { fmtDate } = useLocale();

  useEffect(() => {
    let alive = true;
    const start = Number(evidence.char_start ?? evidence.locator?.char_start ?? 0);
    const end = Number(evidence.char_end ?? evidence.locator?.char_end ?? start + evidence.quoted_span.length);
    documentsApi
      .span(evidence.document_id, start, end, 700)
      .then((s) => alive && setSpan(s))
      .catch((e: Error) => alive && setError(e.message));
    return () => { alive = false; };
  }, [evidence]);

  return (
    // A floating layer is the ONE place a shadow is legal (LAW 3).
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{evidence.document_ref} — {t('the span in context')}</h2>
          <button className="close-btn" onClick={onClose} aria-label={t('Close')}>×</button>
        </div>
        <div className="modal-body">
          {!span && !error ? <div className="spinner" /> : null}
          {error ? (
            <EmptyState kind="error" title={t('The document could not be read')} detail={error} />
          ) : null}
          {span ? (
            <>
              <div className="flex gap-8 items-center mb-12">
                <span className="badge badge-accent src-ref">{span.ref}</span>
                <span className="text-sm text-ink-3">{docMeta?.original_filename || span.original_filename}</span>
                {docMeta?.effective_date ? (
                  <span className="voice-figure text-sm text-ink-3">· {fmtDate(docMeta.effective_date)}</span>
                ) : null}
                <span className="src-ref text-ink-3 ml-auto">{t('chars {start}–{end}', { start: span.start, end: span.end })}</span>
              </div>
              <div
                className="bg-inset border rounded-1 p-16 read-quote"
                style={{ maxHeight: '46vh', overflowY: 'auto', whiteSpace: 'pre-wrap', lineHeight: 1.65 }}
              >
                <span className="text-ink-3">{span.before}</span>
                <mark
                  style={{
                    background: 'var(--act-soft)', color: 'var(--act)', padding: '1px 2px',
                    borderRadius: 'var(--r1)', boxShadow: 'inset 0 -1px 0 var(--act-line)', fontWeight: 600,
                  }}
                >
                  {span.span}
                </mark>
                <span className="text-ink-3">{span.after}</span>
              </div>
              <p className="pq-rat mt-12">
                {t('The quoted span is validated as a literal substring of {ref} at propose time and again on accept — an agent cannot file a quotation this document does not contain.', { ref: evidence.document_ref })}
              </p>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ───────────────────────── the rail ─────────────────────────

/** Five cells, fixed set, fixed order, in every state including empty and degraded. */
interface QueueRailCell extends RailCell {
  key: 'UNHELD' | 'COLLIDING' | 'UNSOURCED' | 'FLAGGED' | 'SESSION';
}

function RailStrip({ cells, meter }: { cells: QueueRailCell[]; meter: number | null }) {
  const t = useT();
  const toneClass = (tone: RailTone) =>
    tone === 'attend' || tone === 'warn' ? 'fv--attend' : tone === 'ok' ? 'fv--ok' : tone === 'nil' ? 'fv--nil' : '';
  return (
    <div className="instr">
    <div className="rail">
      {cells.map((c) => (
        <div className="fld" key={c.key}>
          <div className="fl u-label">{t(c.key)}</div>
          <div className={`fv ${toneClass(c.tone)}`}>
            {/* `nil` (counted, and the count is zero) and `unknown` (not counted) are two
                different facts and must never render the same way — B8. */}
            {c.tone === 'unknown' ? <span className="n-unknown" title={c.detail}>?</span>
              : c.tone === 'nil' && c.value === '—' ? <span className="n-none">—</span>
                : <span className="voice-figure">{c.value}</span>}
            {c.unit ? <span className="u">{c.unit}</span> : null}
          </div>
          <div className="fd">{c.detail}</div>
          {c.key === 'SESSION' && meter !== null ? (
            <div className="pq-meter"><i style={{ width: `${meter}%` }} /></div>
          ) : null}
        </div>
      ))}
    </div>
    </div>
  );
}

// ───────────────────────── session ledger ─────────────────────────

interface Disposition {
  proposal: Proposal;
  verdict: 'held' | 'amended' | 'refused';
  /** The ref the accept actually minted, straight from the server's answer. */
  ref: string | null;
  reason: string | null;
  against: string | null;
  at: number;
}

// ───────────────────────── the page ─────────────────────────

export default function ProposalsPage() {
  const nav = useEngagement();
  const { current } = nav;
  const openRoute = useOpenRoute(nav);
  const toast = useToast();
  const engagementId = current?.id ?? null;
  const t = useT();
  const { tp, fmtDate } = useLocale();
  const dateShort = (iso: string | null | undefined): string => (iso ? fmtDate(String(iso).slice(0, 10)) : '—');

  const [tab, setTab] = useState<RailTab | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('run');
  const [runFilter, setRunFilter] = useState<number | null>(null);
  const [cursorId, setCursorId] = useState<number | null>(null);
  const [ledger, setLedger] = useState<Disposition[]>([]);
  const [mode, setMode] = useState<'amend' | 'resolve' | 'reasons' | null>(null);
  const [side, setSide] = useState<'a' | 'b'>('b');
  const [edits, setEdits] = useState<Record<string, unknown>>({});
  const [editNote, setEditNote] = useState('');
  const [reasonNote, setReasonNote] = useState('');
  const [openRuns, setOpenRuns] = useState<Record<number, boolean>>({});
  const [spanEvidence, setSpanEvidence] = useState<ProposalEvidence | null>(null);
  const [bulk, setBulk] = useState<{ runId: number | null; items: Proposal[] } | null>(null);
  /** The claim the operator is about to DELETE outright (pending only — refuse keeps a reason). */
  const [deleting, setDeleting] = useState<Proposal | null>(null);
  const [busy, setBusy] = useState(false);
  const [requirements, setRequirements] = useState<RegisterRow[]>([]);

  const rowRefs = useRef<Record<number, HTMLButtonElement | null>>({});
  /** Where the cursor stood on each tab, so switching away and back returns to the same claim. */
  const cursorByTab = useRef<Record<RailTab, number | null>>({ untreated: null, held: null, treated: null });
  /** The queue's denominator for THIS session: the unheld count when the page first painted. */
  const sessionTotal = useRef<number | null>(null);

  // ── the two reads, in one poll (numbers are never blanked on a failed refresh) ──
  const load = useCallback(async () => {
    if (!engagementId) throw new Error('No engagement selected.');
    // Every status in one read: the two tabs split it client-side, so both counts are exact
    // (this session's dispositions included) and switching tabs never waits on the network.
    // scope 'all': the thread-held conversation proposals come too, flagged `queued: false`,
    // so the HELD tab can show what the queue predicate keeps out of UNTREATED.
    const [queue, context] = await Promise.all([
      agentApi.listProposals(engagementId, { status: 'all', scope: 'all' }),
      proposalsApi.context(engagementId),
    ]);
    return { queue, context };
  }, [engagementId]);

  const { data, loading, error, notice, refresh } = usePoll(load, POLL_MS);

  const ctx: ProposalsContext | null = data?.context ?? null;
  const proposals = useMemo(() => data?.queue.proposals ?? [], [data]);
  const runs: RunHealth[] = useMemo(() => data?.queue.runs ?? [], [data]);

  // The register list is fetched for ONE reason the study kept: the conflict resolver's
  // "choose a different requirement" escape hatch. The collision lane no longer needs it —
  // the server scores the neighbourhood.
  useEffect(() => {
    if (!engagementId) return;
    registerApi.list(engagementId).then((r) => setRequirements(r.rows)).catch(() => setRequirements([]));
  }, [engagementId]);

  // ── F1 receiver: `run` scopes the queue to one run, `status` picks the tab. One-shot. ──
  const params = useRouteParams();
  useDeepLink(params, {
    status: (v) => setTab(v === 'pending' ? 'untreated' : 'treated'),
    run: (v) => { const n = Number(v); if (Number.isInteger(n) && n > 0) setRunFilter(n); },
  });

  // ── display rows: the server's answer, plus what this session disposed of ──────────────
  // A row the operator just held leaves the UNTREATED tab at once and stands on the TREATED tab,
  // greyed, with its disposition and its undo. It is kept here — not only read back from the
  // server — so it is on the rail between the click and the next poll: that is what a session
  // ledger IS.
  const rows = useMemo(() => {
    const seen = new Set(proposals.map((p) => p.id));
    const kept = ledger.map((d) => d.proposal).filter((p) => !seen.has(p.id));
    const all = [...proposals, ...kept].filter((p) => runFilter === null || p.run_id === runFilter);
    return all.sort((a, b) => (b.run_id ?? -1) - (a.run_id ?? -1) || a.id - b.id);
  }, [proposals, ledger, runFilter]);

  const dispositionOf = useCallback(
    (id: number) => ledger.find((d) => d.proposal.id === id) ?? null,
    [ledger],
  );

  /** Claims still standing unheld IN THE QUEUE — the queue's true subject. */
  const liveRows = useMemo(
    () => rows.filter((p) => p.status === 'pending' && p.queued !== false && !dispositionOf(p.id)),
    [rows, dispositionOf],
  );
  /** Pending claims a workbench/planner thread holds in place (utils/proposalQueue.ts) —
   *  listed here so nothing pending is ever "nowhere"; hold/refuse work the same. */
  const heldRows = useMemo(
    () => rows.filter((p) => p.status === 'pending' && p.queued === false && !dispositionOf(p.id)),
    [rows, dispositionOf],
  );
  /** Everything that carries a disposition — the server's, or this session's. */
  const treatedRows = useMemo(
    () => rows.filter((p) => !(p.status === 'pending' && !dispositionOf(p.id))),
    [rows, dispositionOf],
  );
  const activeTab: RailTab = tab ?? (liveRows.length ? 'untreated' : heldRows.length ? 'held' : 'treated');
  /** What the rail shows and the cursor walks — the active tab, in the chosen order. */
  const visibleRows = useMemo(
    () => sortRows(activeTab === 'untreated' ? liveRows : activeTab === 'held' ? heldRows : treatedRows, sortKey),
    [activeTab, liveRows, heldRows, treatedRows, sortKey],
  );

  // ── the register comparison, and the twins that never reach the register ───────────────
  const checked = ctx ? ctx.neighbours !== null : false;
  // The server scores STANDING claims only, and writes [] for one it scored and found nothing
  // near. An id it did not score (a disposed claim) is null — "not checked" — never [] — "new
  // ground": the stand names the difference.
  const neighboursOf = useCallback(
    (id: number): Neighbour[] | null => (ctx?.neighbours ? ctx.neighbours[String(id)] ?? null : null),
    [ctx],
  );

  /**
   * §3.2 — the within-batch twin. Same similarity function as the server's, run pairwise inside
   * ONE run over the unheld set. The largest URSC run filed 40 proposals: 780 comparisons,
   * sub-millisecond. This is the check that would have stopped #164/#171 → REQ-017.
   */
  const twinIndex = useMemo(() => {
    const out = new Map<number, Twin[]>();
    const byRun = new Map<number | null, Proposal[]>();
    for (const p of liveRows) {
      const key = p.run_id ?? null;
      if (!byRun.has(key)) byRun.set(key, []);
      byRun.get(key)!.push(p);
    }
    for (const group of byRun.values()) {
      for (let i = 0; i < group.length; i += 1) {
        for (let j = i + 1; j < group.length; j += 1) {
          if (group[i].kind !== group[j].kind) continue;
          // An amendment's subject is a label (ref · fields), not prose — trigram over it is
          // meaningless and pairs unrelated deliverables. Two amendments are twins only when
          // they touch the SAME row.
          const sim = isAmendmentKind(group[i].kind)
            ? (amendmentRef(group[i].payload as Record<string, unknown>) === amendmentRef(group[j].payload as Record<string, unknown>)
               && amendmentRef(group[i].payload as Record<string, unknown>) !== null ? 1 : 0)
            : trigramSimilarity(subjectOf(group[i]), subjectOf(group[j]));
          if (sim < SIM_HIT) continue;
          (out.get(group[i].id) ?? out.set(group[i].id, []).get(group[i].id)!).push({ id: group[j].id, sim });
          (out.get(group[j].id) ?? out.set(group[j].id, []).get(group[j].id)!).push({ id: group[i].id, sim });
        }
      }
    }
    return out;
  }, [liveRows]);

  const twinsOf = useCallback((id: number): Twin[] => twinIndex.get(id) ?? [], [twinIndex]);

  const collides = useCallback(
    (p: Proposal): boolean => {
      const n = neighboursOf(p.id);
      return (!!n && !!n[0] && n[0].sim >= SIM_HIT) || twinsOf(p.id).length > 0;
    },
    [neighboursOf, twinsOf],
  );

  // ── cursor: an id, not an index, so a refresh never moves the stand under the operator ──
  useEffect(() => {
    if (!visibleRows.length) { setCursorId(null); return; }
    if (cursorId === null || !visibleRows.some((p) => p.id === cursorId)) setCursorId(visibleRows[0].id);
  }, [visibleRows, cursorId]);
  useEffect(() => { cursorByTab.current[activeTab] = cursorId; }, [cursorId, activeTab]);

  // Keep the standing row in view whenever the cursor moves by something other than j/k — a tab
  // switch, the auto-advance after a disposition, "Stand on #twin", an undo.
  useEffect(() => {
    if (cursorId !== null) rowRefs.current[cursorId]?.scrollIntoView({ block: 'nearest' });
  }, [cursorId, activeTab]);

  /** The operator picks a tab: switch, and stand where they last stood on it (the cursor effect falls back if that claim has since left). */
  const pickTab = useCallback((t: RailTab) => {
    setTab(t);
    const back = cursorByTab.current[t];
    if (back !== null) setCursorId(back);
  }, []);

  const cursorIndex = visibleRows.findIndex((p) => p.id === cursorId);
  const stood = cursorIndex >= 0 ? visibleRows[cursorIndex] : null;

  useEffect(() => { setMode(null); setEdits({}); setEditNote(''); setReasonNote(''); }, [cursorId]);

  useEffect(() => {
    if (sessionTotal.current === null && data) sessionTotal.current = liveRows.length;
  }, [data, liveRows.length]);

  // The automatic tab is resolved ONCE, when the first read lands — never re-derived on a poll,
  // because a poll that files a new claim must not flip the rail and swap the stand under the
  // operator (the same invariant the id-based cursor exists for). A deep-linked choice wins.
  useEffect(() => {
    if (data) setTab((t) => t ?? (liveRows.length ? 'untreated' : 'treated'));
  }, [data, liveRows.length]);

  const moveCursor = useCallback((delta: number) => {
    if (!visibleRows.length) return;
    const at = visibleRows.findIndex((p) => p.id === cursorId);
    const next = Math.min(Math.max((at < 0 ? 0 : at) + delta, 0), visibleRows.length - 1);
    setCursorId(visibleRows[next].id);
    rowRefs.current[visibleRows[next].id]?.scrollIntoView({ block: 'nearest' });
  }, [visibleRows, cursorId]);

  /**
   * After a disposition, stand on the next claim that still needs one. Returns false when none is
   * left — the caller then moves the rail to TREATED: the operator's own last disposition is the
   * one moment the tab changes without a click.
   */
  const standOnNextUnheld = useCallback((justDone: number): boolean => {
    const at = rows.findIndex((p) => p.id === justDone);
    const rest = [...rows.slice(at + 1), ...rows.slice(0, Math.max(at, 0))];
    const next = rest.find((p) => p.status === 'pending' && p.id !== justDone && !dispositionOf(p.id));
    if (next) setCursorId(next.id);
    return !!next;
  }, [rows, dispositionOf]);

  // ── the write preview ────────────────────────────────────────────────────────────────────
  /**
   * The ref this click will mint, advanced by the accepts already banked in this session.
   * ADVISORY, exactly like the counter itself under concurrency — it is stated as "what this
   * click will write", never as a reservation. G-11: a missing `ref_counters` row reads 1.
   */
  const writesRef = useCallback((p: Proposal): string => {
    if (!ctx) return '—';
    const pad = (kind: string, n: number) => `${kind}-${String(n).padStart(ctx.ref_pad[kind] ?? 3, '0')}`;
    const bankedBefore = (predicate: (q: Proposal) => boolean) =>
      ledger.filter((d) => d.verdict !== 'refused' && predicate(d.proposal)).length;

    if (p.kind === 'create_requirement') {
      return pad('REQ', (ctx.next_refs.REQ ?? 1) + bankedBefore((q) => q.kind === 'create_requirement'));
    }
    if (p.kind === 'create_deliverable') {
      return pad('DEL', (ctx.next_refs.DEL ?? 1) + bankedBefore((q) => q.kind === 'create_deliverable'));
    }
    if (p.kind === 'create_test_case') {
      return pad('TC', (ctx.next_refs.TC ?? 1) + bankedBefore((q) => q.kind === 'create_test_case'));
    }
    if (p.kind === 'open_question') {
      return pad('Q', (ctx.next_refs.Q ?? 1) + bankedBefore((q) => q.kind === 'open_question'));
    }
    if (p.kind === 'create_ac') {
      const parentRef = String(p.payload.requirement_ref ?? p.target_ref ?? '');
      const parent = ctx.ac_parents[parentRef];
      const already = parent ? parent.ac_count : 0;
      const banked = bankedBefore((q) => q.kind === 'create_ac' && String(q.payload.requirement_ref ?? '') === parentRef);
      return parentRef ? `${parentRef.replace(/^REQ-/, 'AC-')}.${already + banked + 1}` : '—';
    }
    if (p.kind === 'conflict') {
      return p.target_ref ?? pad('REQ', (ctx.next_refs.REQ ?? 1) + bankedBefore((q) => q.kind === 'create_requirement'));
    }
    return p.target_ref ?? '—';
  }, [ctx, ledger]);

  /** Restates, per branch, what `applyProposal()` actually does. Keeping the two in step is a
      code-review obligation, not machinery — so it is written as one sentence per kind. */
  const writesWhat = (p: Proposal): string => {
    const ev = p.evidence.length;
    switch (p.kind) {
      case 'create_requirement':
        return t('a draft requirement, its revision 1 and {links}', { links: ev ? tp(ev, '{n} evidence link', '{n} evidence links') : t('no evidence link') });
      case 'create_deliverable':
        return t('a deliverable and {links}', { links: tp((p.payload.satisfies_refs ?? []).length, '{n} requirement link', '{n} requirement links') });
      case 'create_ac':
        return t('an acceptance criterion under {ref}', { ref: String(p.payload.requirement_ref ?? p.target_ref ?? '') || t('its requirement') });
      case 'create_test_case':
        return t('a test case and {links}', { links: tp((p.payload.ac_refs ?? []).length, '{n} criterion link', '{n} criterion links') });
      case 'update_requirement':
        return t('a new revision of {ref} — the previous one is kept, never overwritten', { ref: p.target_ref ?? t('the anchored requirement') });
      case 'conflict':
        return p.target_ref
          ? t('a new revision of {ref} carrying the winning side', { ref: p.target_ref })
          : t('a draft requirement carrying the winning side');
      case 'open_question':
        return t('an open question on the client-call agenda');
      default:
        return t('a register row');
    }
  };

  // ── mutations ────────────────────────────────────────────────────────────────────────────
  const hold = useCallback(
    async (p: Proposal, opts: { edits?: Record<string, unknown>; note?: string; discuss?: boolean } = {}) => {
      setBusy(true);
      try {
        const result = await agentApi.accept(p.id, {
          edits: opts.edits,
          note: opts.note,
          ...(opts.discuss ? { discuss_with_client: true } : {}),
        });
        const ref = (result.result as { created_ref?: string }).created_ref ?? writesRef(p);
        const oq = (result.result as { discuss_open_question?: { ref?: string } }).discuss_open_question;
        setLedger((prev) => [
          { proposal: p, verdict: opts.edits ? 'amended' : 'held', ref, reason: null, against: null, at: Date.now() },
          ...prev.filter((d) => d.proposal.id !== p.id),
        ]);
        toast('ok', `${t('#{id} held', { id: p.id })}${ref ? ` → ${ref}` : ''}${oq?.ref ? ` · ${t('{ref} on the client-call agenda', { ref: oq.ref })}` : ''}`);
        setMode(null);
        if (!standOnNextUnheld(p.id)) setTab('treated');
        await refresh();
      } catch (err) {
        toast('err', err instanceof Error ? err.message : t('The hold failed — nothing was written.'));
      } finally {
        setBusy(false);
      }
    },
    [refresh, toast, writesRef, standOnNextUnheld, t],
  );

  const refuse = useCallback(
    async (p: Proposal, reason: string, against: string | null, note: string) => {
      setBusy(true);
      try {
        // §4.4a — the reference the refusal collides with is recorded in the note the route
        // already accepts. Zero machinery, and the aggregate can read it.
        const composed = [against ? `against ${against}` : '', note].filter(Boolean).join(' — ');
        await agentApi.reject(p.id, reason, composed || undefined);
        setLedger((prev) => [
          { proposal: p, verdict: 'refused', ref: null, reason, against, at: Date.now() },
          ...prev.filter((d) => d.proposal.id !== p.id),
        ]);
        toast('warn', `${t('#{id} refused — {reason}', { id: p.id, reason: t(REJECT_REASON_LABEL[reason] ?? reason) })}${against ? ` (${against})` : ''}`);
        setMode(null);
        setReasonNote('');
        if (!standOnNextUnheld(p.id)) setTab('treated');
        await refresh();
      } catch (err) {
        toast('err', err instanceof Error ? err.message : t('The refusal failed — nothing was written.'));
      } finally {
        setBusy(false);
      }
    },
    [refresh, toast, standOnNextUnheld, t],
  );

  const undo = useCallback(
    async (d: Disposition) => {
      setBusy(true);
      try {
        // Where the cursor goes is decided BEFORE the row moves. The view stays on the tab the
        // operator is working in: on TREATED the undone row leaves and the cursor stands on the
        // neighbour it leaves behind (so the operator keeps walking the list); on UNTREATED the
        // row comes back and the cursor stands on it.
        const at = visibleRows.findIndex((p) => p.id === d.proposal.id);
        const neighbour = at >= 0 ? (visibleRows[at + 1] ?? visibleRows[at - 1] ?? null) : null;
        // Two different undos, because two different things happened. A refusal wrote a status
        // and a reason and nothing else, so it is walked back whole. An accept created a
        // register row, and `revert` is the endpoint that knows how to delete it — and that
        // refuses when something already depends on it.
        const out = d.verdict === 'refused'
          ? await proposalsApi.unrefuse(d.proposal.id)
          : await agentApi.revertProposal(d.proposal.id);
        toast('ok', out.note);
        setLedger((prev) => prev.filter((x) => x.proposal.id !== d.proposal.id));
        await refresh();
        // Asserted AFTER the refresh: until the server answers, the row is still 'accepted' in
        // `proposals` and the cursor effect would snap to another row.
        if (activeTab === 'treated') { if (neighbour) setCursorId(neighbour.id); }
        else setCursorId(d.proposal.id);
      } catch (err) {
        // revert refuses when something already depends on what the accept created. Say so.
        toast('err', err instanceof Error ? err.message : t('The undo was refused.'));
      } finally {
        setBusy(false);
      }
    },
    [refresh, toast, visibleRows, activeTab, t],
  );

  const holdAll = useCallback(async () => {
    if (!bulk) return;
    const items = bulk.items;
    setBulk(null);
    setBusy(true);
    let done = 0;
    const refused: string[] = [];
    for (const p of items) {
      try {
        const result = await agentApi.accept(p.id, {});
        const ref = (result.result as { created_ref?: string }).created_ref ?? null;
        setLedger((prev) => [
          { proposal: p, verdict: 'held', ref, reason: null, against: null, at: Date.now() },
          ...prev.filter((d) => d.proposal.id !== p.id),
        ]);
        done += 1;
      } catch (err) {
        refused.push(`#${p.id}: ${err instanceof Error ? err.message : t('refused')}`);
      }
    }
    setBusy(false);
    // F7.1's shared voice: what was done, what was refused, and why — never a bare count.
    toast(refused.length ? 'warn' : 'ok', `${tp(done, '{n} claim held', '{n} claims held')}${refused.length ? `, ${t('{n} refused and still unheld', { n: refused.length })}` : ''}`);
    for (const line of refused.slice(0, 3)) toast('err', line);
    // The bulk hold was the operator's own last disposition of the standing set → TREATED.
    if (!refused.length && liveRows.every((p) => items.some((i) => i.id === p.id))) setTab('treated');
    await refresh();
  }, [bulk, liveRows, refresh, toast, t, tp]);

  /** Delete outright: gone from the queue with no refusal record — the audit entry keeps it. */
  const deleteForever = useCallback(async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await agentApi.deleteProposal(deleting.id);
      toast('ok', t('#{id} deleted — the audit log keeps its payload', { id: deleting.id }));
      setDeleting(null);
      await refresh();
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('The delete was refused.'));
      setDeleting(null);
    } finally {
      setBusy(false);
    }
  }, [deleting, refresh, toast, t]);

  // ── keyboard ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) {
        if (event.key === 'Escape') { setMode(null); (target as HTMLElement).blur(); }
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (spanEvidence || bulk) {
        if (event.key === 'Escape') { setSpanEvidence(null); setBulk(null); }
        return;
      }
      const k = event.key.toLowerCase();
      if (event.key === 'Escape') { setMode(null); return; }
      if (k === 'j' || event.key === 'ArrowDown') { event.preventDefault(); moveCursor(1); return; }
      if (k === 'k' || event.key === 'ArrowUp') { event.preventDefault(); moveCursor(-1); return; }
      if (k === 'u') {
        event.preventDefault();
        if (ledger[0]) void undo(ledger[0]);
        return;
      }
      if (!stood || stood.status !== 'pending' || dispositionOf(stood.id)) return;
      if (k === 'a') {
        event.preventDefault();
        if (stood.kind === 'conflict' && mode !== 'resolve') { setMode('resolve'); setSide('b'); }
        else void hold(stood);
      } else if (k === 'e') {
        event.preventDefault();
        setMode(mode === 'amend' ? null : 'amend');
      } else if (k === 'r') {
        event.preventDefault();
        setMode(mode === 'reasons' ? null : 'reasons');
      } else if (k === 'c' && DISCUSS_KINDS.includes(stood.kind)) {
        event.preventDefault();
        void hold(stood, { discuss: true });
      } else if (k === 'd') {
        event.preventDefault();
        const twins = twinsOf(stood.id);
        const top = neighboursOf(stood.id)?.[0];
        if (twins.length) void refuse(stood, 'duplicate', `#${twins[0].id}`, 'the run filed it twice');
        else if (top && top.sim >= SIM_HIT) void refuse(stood, 'duplicate', top.ref, '');
        else toast('warn', t('Nothing collides with this claim — d only fires when the page is showing you what it duplicates.'));
      } else if (k === 'v') {
        event.preventDefault();
        if (stood.evidence[0]) setSpanEvidence(stood.evidence[0]);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [stood, mode, ledger, spanEvidence, bulk, moveCursor, hold, refuse, undo, twinsOf, neighboursOf, dispositionOf, toast, t]);

  // ── no engagement ────────────────────────────────────────────────────────────────────────
  if (!current) {
    return (
      <div className="page pq">
        <header className="mast"><div className="mast-top"><div className="mast-main"><h1>{t('Proposals')}</h1></div></div></header>
        <div className="zone">
          <EmptyState
            kind="empty"
            title={t('No engagement selected')}
            detail={t('The review queue is scoped to one engagement — every claim in it names a run, a document and a register it would enter.')}
          />
        </div>
      </div>
    );
  }

  // ── the rail's five cells ────────────────────────────────────────────────────────────────
  const unheld = liveRows.length;
  const runIds = [...new Set(liveRows.map((p) => p.run_id).filter((r): r is number => r !== null))];
  const oldest = liveRows.length
    ? liveRows.map((p) => p.created_at).sort()[0]
    : null;
  const colliding = liveRows.filter((p) => collides(p)).length;
  const unsourced = liveRows.filter((p) => p.evidence_count === 0).length;
  const flagged = liveRows.filter((p) => {
    const lint = p.payload.lint;
    const conf = p.payload.confidence;
    return (!!lint && !lint.pass) || (typeof conf === 'number' && conf < 0.8);
  }).length;
  const disposedCount = ledger.length;
  const denominator = sessionTotal.current ?? unheld + disposedCount;
  const held = ledger.filter((d) => d.verdict !== 'refused').length;
  const refusedCount = ledger.filter((d) => d.verdict === 'refused').length;
  /** Nothing is standing unheld. It is a different fact from "this engagement has no queue". */
  const settled = !loading && unheld === 0;
  /** Nothing is standing unheld AND nothing is on the rail at all — the empty-sheet state. */
  /** Nothing on the rail at all under no run scope — this engagement has never filed a claim. */
  const empty = settled && rows.length === 0 && runFilter === null;

  const cells: QueueRailCell[] = [
    {
      key: 'UNHELD',
      value: settled ? '—' : String(unheld),
      unit: settled ? null : t('claims'),
      // The empty-run SENTENCE is this tab's copy — cutRun([]) deliberately says nothing.
      detail: settled
        ? t('every claim has a disposition')
        : cutRun([tp(runIds.length, '{n} run', '{n} runs'), t('oldest {date}', { date: dateShort(oldest) })]) || t('not attributed to a run'),
      tone: settled ? 'nil' : 'ink',
      route: null,
    },
    {
      key: 'COLLIDING',
      value: !checked ? '?' : settled ? '—' : String(colliding),
      unit: !checked || settled ? null : t('with the record'),
      detail: !checked
        ? t('the comparison did not answer')
        : settled ? t('nothing left to compare') : t('≥ {sim} to a register row', { sim: SIM_HIT.toFixed(2) }),
      tone: !checked ? 'unknown' : settled ? 'nil' : colliding > 0 ? 'attend' : 'ink',
      route: null,
    },
    {
      key: 'UNSOURCED',
      value: settled ? '—' : String(unsourced),
      unit: settled ? null : t('no evidence'),
      detail: settled ? t('nothing unheld') : t('judged against the register instead'),
      tone: settled ? 'nil' : unsourced > 0 ? 'attend' : 'ink',
      route: null,
    },
    {
      key: 'FLAGGED',
      value: settled ? '—' : String(flagged),
      unit: settled ? null : t('lint / low conf'),
      detail: settled ? t('nothing unheld') : t('lint error or confidence < 0.80'),
      tone: settled ? 'nil' : flagged > 0 ? 'attend' : 'ink',
      route: null,
    },
    {
      key: 'SESSION',
      value: disposedCount ? t('{done} of {total}', { done: disposedCount, total: denominator }) : settled ? '—' : t('{done} of {total}', { done: 0, total: denominator }),
      unit: null,
      detail: disposedCount
        ? cutRun([t('{n} held', { n: held }), t('{n} refused', { n: refusedCount })])
        : settled ? t('no queue to work') : t('nothing disposed yet'),
      tone: disposedCount ? 'ink' : settled ? 'nil' : 'ink',
      route: null,
    },
  ];

  // ── the hot-run alert: present ONLY when a run in the queue earned it ────────────────────
  const runOf = (id: number | null) => runs.find((r) => r.id === id) ?? null;
  const hotRun = runIds
    .map((id) => runOf(id))
    .find((r) => {
      if (!r) return false;
      const stats = r.stats as QueueRunStats;
      const rejections = Number(stats.tool_rejections ?? 0);
      const shortOfDocs = stats.close_discipline === 'docs' && r.docs_covered_count < r.docs_in_scope_count;
      return rejections > 3 || shortOfDocs;
    }) ?? null;

  const gate = ctx?.gate ?? null;

  return (
    <div className="page pq">
      {/* ── masthead + the instrument rail ─────────────────────────────────── */}
      <header className="mast">
        <div className="mast-top">
          <div className="mast-main">
            <h1>{t('Proposals')}</h1>
            <p className="mast-sub pq-sub">
              {settled ? (
                <>
                  {t('Every claim the agent has filed in')} <span className="src-ref">{current.code}</span>{' '}
                  {t('carries a human disposition — the register is answerable for all of it.')}
                </>
              ) : (
                <>
                  <span className="voice-figure">{unheld}</span> {t('claims the agent has made in')}{' '}
                  <span className="src-ref">{current.code}</span>{' '}
                  {t('that nobody has stood behind. None of it is in the register yet.')}
                </>
              )}
              {gate && gate.conditions.length ? (
                <span className="pq-gateline">
                  {t('Gate')} <span className="src-ref">{gate.gate}</span> {gate.title} {t('waits on this queue:')}{' '}
                  {gate.conditions.map((c, i) => (
                    <span key={c.label}>
                      {i > 0 ? ' · ' : ''}
                      {/* B7 — the gate engine's own words, rendered unedited. */}
                      {c.label.toLowerCase()} ({c.detail.toLowerCase()})
                    </span>
                  ))}
                </span>
              ) : null}
            </p>
          </div>
          <div className="mast-verbs">
            {notice ? <span className="pq-notice">{notice}</span> : null}
            <span className="pq-keys">{t('j/k · a hold · e amend · r refuse · d duplicate · u undo')}</span>
            <button className="btn btn-sm" onClick={() => void refresh()} disabled={busy}>{t('Refresh')}</button>
          </div>
        </div>
      </header>
      <RailStrip cells={cells} meter={denominator ? Math.round((100 * disposedCount) / denominator) : null} />

      {/* ── zone 1 · the queue ─────────────────────────────────────────────── */}
      <div className="zone" id="queue">
        <div className="zone-head">
          <span className="u-label">{t('The queue')}</span>
          <span className="zone-count">
            {loading ? t('reading') : empty ? t('nothing to review') : `${tp(unheld, '{n} claim unheld', '{n} claims unheld')} · ${t('{n} disposed this session', { n: disposedCount })}`}
          </span>
        </div>

        {ctx?.problems.length ? (
          <ProblemState problems={ctx.problems} source="register-comparison" zone={t('The register comparison')} onRetry={refresh} />
        ) : null}

        {!checked && ctx ? (
          <div className="pq-alert">
            <span className="u-label" style={{ color: 'var(--attend)' }}>{t('Register comparison unavailable')}</span>
            <span className="note">
              {t('Every claim below is unchecked against the record. Holding one still writes; it just writes without the duplicate and contradiction check. The queue does not pretend the check passed.')}
            </span>
          </div>
        ) : hotRun ? (
          <div className="pq-alert">
            <span className="src-ref">{t('run {id}', { id: hotRun.id })}</span>
            <span className="note">
              {t('the tool layer pushed back')}{' '}
              <span className="voice-figure">{Number((hotRun.stats as QueueRunStats).tool_rejections ?? 0)}</span>{' '}
              {t('times on this run')}
              {(hotRun.stats as QueueRunStats).close_discipline === 'docs'
                && hotRun.docs_covered_count < hotRun.docs_in_scope_count
                ? tp(hotRun.docs_in_scope_count, ', and it cited {covered} of the {n} document it was given', ', and it cited {covered} of the {n} documents it was given', { covered: hotRun.docs_covered_count })
                : ''}
              {'. '}{t('Read the evidence closely.')}
            </span>
          </div>
        ) : null}

        {runFilter !== null ? (
          <div className="pq-alert" style={{ borderLeftColor: 'var(--act)' }}>
            <span className="u-label">{t('Scoped to run {run}', { run: runFilter })}</span>
            <span className="pq-note">{t('You arrived here from a link that named one run.')}</span>
            <button className="btn btn-sm ml-auto" onClick={() => setRunFilter(null)}>{t('Show every run')}</button>
          </div>
        ) : null}

        {error && !data ? (
          <EmptyState
            kind="error"
            title={t('The review queue could not be read')}
            detail={error}
            onRetry={refresh}
          />
        ) : loading ? (
          <div className="sheet sheet--strong pq-panes">
            <div className="pq-qrail">
              <div className="pq-qwrap">
                <div className="pq-qtabs"><span className="pq-qtab" aria-hidden="true">{t('reading the queue')}</span></div>
                <div className="pq-qscroll">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <div key={i} style={{ padding: '12px' }}>
                      <div className="pq-skel w40" /><div className="pq-skel w70" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="pq-stand"><div className="pq-stbody"><div className="pq-skel w40" /><div className="pq-skel" /><div className="pq-skel w70" /></div></div>
          </div>
        ) : empty ? (
          <div className="sheet sheet--strong pq-empty">
            <h3>{ctx && ctx.history.total > 0 ? t('Nothing unheld.') : t('Nothing filed yet.')}</h3>
            <p>
              {ctx && ctx.history.total > 0 ? (
                <>
                  {t('This engagement has filed')} <span className="voice-figure">{ctx.history.total}</span>{' '}
                  {t('claims. Every one carries a human disposition:')} <span className="voice-figure">{ctx.history.held}</span> {t('held,')}{' '}
                  <span className="voice-figure">{ctx.history.amended}</span> {t('amended before holding,')}{' '}
                  <span className="voice-figure">{ctx.history.refused}</span>{' '}
                  {t('refused. The register is answerable for all of them.')}
                </>
              ) : (
                t('No claim has been filed for this engagement yet. An extraction pass reads the documents in scope and files what it finds here — nothing reaches the register until you hold it.')
              )}
            </p>
            {gate ? (
              <p className="pq-rat" style={{ marginTop: 12 }}>
                {t('Gate')} <span className="src-ref">{gate.gate}</span> {gate.title} {t('asks for')}{' '}
                {gate.conditions.map((c) => c.label.toLowerCase()).join(` ${t('and')} `)}.{' '}
                {gate.conditions.every((c) => c.met) ? t('This half of it is met.') : t('It is not met yet.')}
              </p>
            ) : null}
            <div className="row">
              <button className="btn btn-accent" onClick={() => void openRoute(ROUTES.agent({ engagement: current.id }))}>
                {t('Run an extraction pass →')}
              </button>
              <button className="btn" onClick={() => void openRoute(ROUTES.register({ engagement: current.id }))}>
                {t('Open the register')}
              </button>
            </div>
          </div>
        ) : !rows.length ? (
          <EmptyState
            kind="no-match"
            title={t('No claim matches this filter')}
            detail={t('The queue is scoped to run {run}, and that run filed nothing.', { run: runFilter })}
            onClear={() => setRunFilter(null)}
            clearLabel={t('Show every run')}
          />
        ) : (
          <div className="sheet sheet--strong pq-panes">
            {/* ── the queue rail ───────────────────────────────────────────── */}
            <div className="pq-qrail">
              <div className="pq-qwrap">
                <div
                  className="pq-qtabs"
                  role="tablist"
                  aria-label={t('Claims')}
                  onKeyDown={(e) => {
                    // Left/Home → UNTREATED, Right/End → TREATED: the roving-tabindex tablist pattern.
                    const order: RailTab[] = ['untreated', 'held', 'treated'];
                    const at = order.indexOf(activeTab);
                    const next: RailTab | null =
                      e.key === 'Home' ? 'untreated' : e.key === 'End' ? 'treated'
                        : e.key === 'ArrowLeft' ? order[Math.max(0, at - 1)]
                          : e.key === 'ArrowRight' ? order[Math.min(order.length - 1, at + 1)] : null;
                    if (!next) return;
                    e.preventDefault();
                    pickTab(next);
                    (e.currentTarget.querySelector(`#pq-qtab-${next}`) as HTMLElement | null)?.focus();
                  }}
                >
                  {(['untreated', 'held', 'treated'] as RailTab[]).map((tb) => {
                    const n = tb === 'untreated' ? liveRows.length : tb === 'held' ? heldRows.length : treatedRows.length;
                    return (
                      <button
                        key={tb}
                        id={`pq-qtab-${tb}`}
                        type="button"
                        role="tab"
                        aria-selected={activeTab === tb}
                        aria-controls="pq-qpanel"
                        tabIndex={activeTab === tb ? 0 : -1}
                        className={`pq-qtab${activeTab === tb ? ' on' : ''}`}
                        onClick={() => pickTab(tb)}
                      >
                        {t(TAB_LABEL[tb])}<span className="n voice-figure">({n})</span>
                      </button>
                    );
                  })}
                </div>
                <div className="pq-qsort" role="group" aria-label={t('Sort the rail')}>
                  <span className="u-label">{t('sort')}</span>
                  {SORT_KEYS.map((k) => (
                    <button
                      key={k.key}
                      type="button"
                      className={`pq-qsortk${sortKey === k.key ? ' on' : ''}`}
                      aria-pressed={sortKey === k.key}
                      onClick={() => setSortKey(k.key)}
                    >
                      {t(k.label)}
                    </button>
                  ))}
                </div>
                <div className="pq-qscroll" id="pq-qpanel" role="tabpanel" aria-labelledby={`pq-qtab-${activeTab}`}>
                  {!visibleRows.length ? (
                    <div className="pq-qempty">
                      {activeTab === 'untreated' ? t('nothing untreated')
                        : activeTab === 'held' ? t('nothing held in a thread') : t('nothing treated yet')}
                    </div>
                  ) : null}
                  {visibleRows.map((p, i) => {
                    const group = groupOf(p, sortKey, t);
                    const prev = i > 0 ? visibleRows[i - 1] : null;
                    const newGroup = !prev || groupOf(prev, sortKey, t).id !== group.id;
                    // The group line counts across BOTH tabs: "3 of 12 unheld" under a treated row
                    // still tells you that group has three claims standing.
                    const mine = rows.filter((q) => groupOf(q, sortKey, t).id === group.id);
                    const left = mine.filter((q) => q.status === 'pending' && !dispositionOf(q.id)).length;
                    const d = dispositionOf(p.id);
                    const f = flagsFor(p, neighboursOf(p.id), twinsOf(p.id), checked, { t, tp });
                    const a = attrOf(p);
                    const subject = subjectOf(p);
                    return (
                      <div key={p.id}>
                        {newGroup ? (
                          <div className="pq-qgrp">
                            <span className={sortKey === 'run' ? 'src-ref' : undefined}>{group.label}</span>
                            <span className="n">{t('{left} of {total} unheld', { left, total: mine.length })}</span>
                          </div>
                        ) : null}
                        <button
                          ref={(el) => { rowRefs.current[p.id] = el; }}
                          className={`pq-qrow${p.id === cursorId ? ' on' : ''}`}
                          onClick={() => setCursorId(p.id)}
                          title={p.descriptor ? subject : undefined}
                        >
                          <span
                            className={`att ${d ? (d.verdict === 'refused' ? '' : 'att--held')
                              : p.status === 'rejected' ? '' : p.status === 'pending' ? 'att--proposed' : 'att--held'}`}
                            aria-hidden="true"
                          />
                          <span>
                            <span className="qk">{t(KIND_NOUN[p.kind] ?? p.kind)}</span>
                            {/* the ≤20-word descriptor the model wrote for the rail — the subject itself
                                until it exists, and always on hover. The stand shows the statement verbatim. */}
                            <span className="qs">{p.descriptor ?? (subject.slice(0, 160) || `#${p.id}`)}</span>
                          </span>
                          <span className="pq-qattr" aria-label={t('type, weight, category')}>
                            {a.type ? <span>{t(typeWord(a.type))}</span> : null}
                            {a.priority ? <span className="w">{t(weightWord(a.priority))}</span> : null}
                            {a.category ? <span title={a.category}>{a.category}</span> : null}
                          </span>
                          <span className={`pq-qflag ${f[0]?.tone ?? 'mute'}`}>
                            {d || p.status !== 'pending' ? t(dispositionWord(p.status, d)) : (f[0]?.short ?? '')}
                          </span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* ── the stand ────────────────────────────────────────────────── */}
            <div className="pq-stand">
              {stood ? (
                <Stand
                  proposal={stood}
                  position={t('{i} of {total}', { i: cursorIndex + 1, total: visibleRows.length })}
                  disposition={dispositionOf(stood.id)}
                  neighbours={neighboursOf(stood.id)}
                  checked={checked || !TRIGRAM_KINDS.has(stood.kind)}
                  twins={twinsOf(stood.id).map((t) => ({ twin: t, row: rows.find((q) => q.id === t.id) ?? null }))}
                  ctx={ctx}
                  requirements={requirements}
                  busy={busy}
                  mode={mode}
                  side={side}
                  edits={edits}
                  editNote={editNote}
                  reasonNote={reasonNote}
                  writesRef={writesRef(stood)}
                  writesWhat={writesWhat(stood)}
                  onSetMode={setMode}
                  onSetSide={setSide}
                  onSetEdits={setEdits}
                  onSetEditNote={setEditNote}
                  onSetReasonNote={setReasonNote}
                  onHold={hold}
                  onRefuse={refuse}
                  onUndo={undo}
                  onDelete={setDeleting}
                  onSpan={setSpanEvidence}
                  onGoto={setCursorId}
                  onOpenRef={(ref) => void openRoute(ROUTES.register({ engagement: current.id, ref }))}
                />
              ) : (
                <div className="pq-stbody">
                  <EmptyState
                    kind="empty"
                    title={activeTab === 'untreated' ? t('Nothing unheld.') : t('Nothing treated yet.')}
                    detail={activeTab === 'untreated'
                      ? `${t('Every claim on file carries a human disposition.')}${treatedRows.length ? ` ${tp(treatedRows.length, 'The {n} treated claim is on the other tab.', 'The {n} treated claims are on the other tab.')}` : ''}`
                      : t('Hold or refuse a claim and it moves here, with its disposition and its undo.')}
                  >
                    {activeTab === 'untreated' && treatedRows.length ? (
                      <button type="button" className="btn btn-sm" onClick={() => pickTab('treated')}>
                        {t('Show the {n} treated →', { n: treatedRows.length })}
                      </button>
                    ) : null}
                  </EmptyState>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── zone 2 · the session ledger ────────────────────────────────────── */}
      <div className="zone" id="ledger">
        <div className="zone-head">
          <span className="u-label">{t('This session')}</span>
          <span className="zone-count">{ledger.length ? t('{n} · newest first', { n: ledger.length }) : ''}</span>
        </div>
        <div className="sheet sheet--clip pq-ledger">
          {ledger.length ? (
            ledger.map((d) => (
              <div className="pq-led" key={d.proposal.id}>
                <span className={`att ${d.verdict === 'refused' ? '' : 'att--held'}`} aria-hidden="true" />
                <span className="lk">{d.verdict === 'refused' ? t('refused') : d.verdict === 'amended' ? t('amended · held') : t('held')}</span>
                <span className="lt">{subjectOf(d.proposal) || `#${d.proposal.id}`}</span>
                <span className="lr">
                  {d.verdict === 'refused'
                    ? `${t(REJECT_REASON_LABEL[d.reason ?? ''] ?? d.reason ?? '')}${d.against ? ` · ${d.against}` : ''}`
                    : <span className="src-ref">{d.ref ?? '—'}</span>}
                </span>
                <button className="btn btn-sm btn-ghost" disabled={busy} onClick={() => void undo(d)}>{t('Undo')}</button>
              </div>
            ))
          ) : (
            <p className="pq-rat">
              {t('Nothing disposed yet. Each disposition lands here with an undo, so a wrong hold is one click away for as long as this session lasts — not a filter change and a scan of')}{' '}
              <span className="voice-figure">{ctx ? ctx.history.held + ctx.history.amended : 0}</span> {t('held rows.')}
            </p>
          )}
        </div>
      </div>

      {/* ── zone 3 · the runs ──────────────────────────────────────────────── */}
      {runIds.length ? (
        <div className="zone" id="runs">
          <div className="zone-head">
            <span className="u-label">{t('The runs behind this queue')}</span>
            <span className="zone-count">{tp(runIds.length, '{n} run with unheld claims', '{n} runs with unheld claims')}</span>
          </div>
          {runIds.map((id) => {
            const run = runOf(id);
            if (!run) return null;
            const stats = run.stats as QueueRunStats;
            const mine = liveRows.filter((p) => p.run_id === id);
            const rejections = Number(stats.tool_rejections ?? 0);
            const discipline = stats.close_discipline;
            const shortOfDocs = discipline === 'docs' && run.docs_covered_count < run.docs_in_scope_count;
            const account = parseClosingAccount(stats.closing_account);
            const colliders = mine.filter((p) => collides(p)).length;
            const open = !!openRuns[id];
            const refusalsHere = (ctx?.refusals ?? []).filter((r) => r.run_id === id);
            return (
              <div className={`pq-run${rejections > 3 || shortOfDocs ? ' attend' : ''}`} key={id}>
                <div className="pq-runhd">
                  <span className="src-ref">{t('run {id}', { id })}</span>
                  <span className="chip">{run.mode}</span>
                  <span className={`chip${['queued', 'starting', 'running'].includes(run.status) ? ' chip--attend' : ''}`}>
                    {['queued', 'starting', 'running'].includes(run.status) ? t('running · will file more') : t(run.status)}
                  </span>
                  <span className="pq-note">
                    <span className="voice-figure">{mine.length}</span> {t('unheld of')}{' '}
                    <span className="voice-figure">{run.proposal_count}</span> {t('filed ·')}{' '}
                    {/* THE COVERAGE UNIT IS THE RUN'S OWN CLOSE DISCIPLINE. A batch run is
                        accountable for DOCUMENTS; a targeted pass is accountable for REGISTER
                        ROWS. Printing "3/25 docs cited · stopped short" against an
                        acceptance-criteria pass was a false alarm on the one signal that
                        means "read closely". */}
                    {discipline === 'docs'
                      ? shortOfDocs
                        ? tp(run.docs_in_scope_count, 'cited {covered} of the {n} document it was given', 'cited {covered} of the {n} documents it was given', { covered: run.docs_covered_count })
                        : tp(run.docs_covered_count, 'cited {n} document', 'cited {n} documents')
                      : discipline
                        ? tp(account.total, 'read {n} register item', 'read {n} register items')
                        : t('no close discipline recorded')}
                  </span>
                  <span className={`pq-note${rejections > 3 ? ' attend' : ''}`}>
                    {rejections
                      ? `${tp(rejections, '{n} tool rejection', '{n} tool rejections')}${rejections > 3 ? ` — ${t('read the evidence closely')}` : ''}`
                      : t('clean tool log')}
                    {' · '}{tp(Number(stats.turns ?? 0), '{n} turn', '{n} turns')}
                  </span>
                  {refusalsHere.length ? (
                    <span className="pq-note">
                      {t('{n} refused', { n: refusalsHere.reduce((n, r) => n + r.n, 0) })}:{' '}
                      {refusalsHere.map((r) => `${r.n} ${t(REJECT_REASON_LABEL[r.reason] ?? r.reason.replace(/_/g, ' '))}`).join(', ')}
                    </span>
                  ) : null}
                  <span className="pq-note src-ref" style={{ marginLeft: 'auto' }}>
                    {(run.model_id ?? '').split('/').pop()}
                  </span>
                  <button
                    className="btn btn-sm btn-ghost"
                    onClick={() => setOpenRuns((prev) => ({ ...prev, [id]: !prev[id] }))}
                  >
                    {open ? t('hide the account') : t('what it says it did')} ▸
                  </button>
                  {!checked ? (
                    <button className="btn btn-sm" disabled title={t('the collision check did not answer — a bulk hold cannot be offered blind')}>
                      {t('Hold all — check unavailable')}
                    </button>
                  ) : colliders ? (
                    <button className="btn btn-sm" disabled title={t('a bulk control must not be able to commit a known collision')}>
                      {t('Hold all — {n} collide', { n: colliders })}
                    </button>
                  ) : mine.length ? (
                    <button className="btn btn-sm" disabled={busy} onClick={() => setBulk({ runId: id, items: mine })}>
                      {t('Hold all {n}', { n: mine.length })}
                    </button>
                  ) : null}
                </div>
                {open ? (
                  <div className="pq-runbody">
                    {account.total ? (
                      <>
                        <div className="pq-acct-head">{accountHead(account, discipline ?? null)}</div>
                        <div className="pq-acct">
                          {account.lines.map((l, i) => (
                            <div key={i} className={l.muted ? 'muted' : ''}>
                              {/^[A-Z]{2,3}-\d/.test(l.key) ? <b>{l.key}</b> : <strong>{l.key}</strong>} {l.text}
                            </div>
                          ))}
                        </div>
                        {account.warning ? <p className="pq-acct-warn">{account.warning}</p> : null}
                      </>
                    ) : (
                      <p className="pq-rat">
                        {t('This run wrote no closing account. The close discipline is enforced at the tool layer')}
                        {' ('}<span className="src-ref">close_pass</span>{') — '}
                        {t('a run without one either predates it or ended before it could satisfy it.')}
                      </p>
                    )}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}

      {spanEvidence ? (
        <SpanSheet
          evidence={spanEvidence}
          docMeta={ctx?.docs[spanEvidence.document_ref]}
          onClose={() => setSpanEvidence(null)}
        />
      ) : null}

      <ConfirmModal
        open={!!bulk}
        title={tp(bulk?.items.length ?? 0, 'Hold {n} claim', 'Hold {n} claims')}
        confirmLabel={t('Hold them all')}
        onCancel={() => setBulk(null)}
        onConfirm={holdAll}
      >
        <p className="read">
          {t('Every unheld claim in run #{run} will be held exactly as proposed, one at a time. Anything the register refuses (a baselined requirement, a failed evidence check) stays unheld and is reported back.', { run: bulk?.runId ?? '—' })}
        </p>
      </ConfirmModal>

      <ConfirmModal
        open={!!deleting}
        title={t('Delete claim #{id}', { id: deleting?.id ?? '' })}
        confirmLabel={busy ? t('Deleting…') : t('Delete permanently')}
        danger
        onCancel={() => setDeleting(null)}
        onConfirm={() => void deleteForever()}
      >
        <p className="read">
          {t('This removes the machine’s claim outright — it never reaches the register and it will NOT appear as refused with a reason, so the next pass cannot learn from it. The audit log keeps its payload. A claim worth an answer is refused, not deleted.')}
        </p>
      </ConfirmModal>
    </div>
  );
}

// ───────────────────────── the stand ─────────────────────────

interface StandProps {
  proposal: Proposal;
  position: string;
  disposition: Disposition | null;
  neighbours: Neighbour[] | null;
  checked: boolean;
  twins: Array<{ twin: Twin; row: Proposal | null }>;
  ctx: ProposalsContext | null;
  requirements: RegisterRow[];
  busy: boolean;
  mode: 'amend' | 'resolve' | 'reasons' | null;
  side: 'a' | 'b';
  edits: Record<string, unknown>;
  editNote: string;
  reasonNote: string;
  writesRef: string;
  writesWhat: string;
  onSetMode: (m: 'amend' | 'resolve' | 'reasons' | null) => void;
  onSetSide: (s: 'a' | 'b') => void;
  onSetEdits: (e: Record<string, unknown>) => void;
  onSetEditNote: (v: string) => void;
  onSetReasonNote: (v: string) => void;
  onHold: (p: Proposal, opts?: { edits?: Record<string, unknown>; note?: string; discuss?: boolean }) => Promise<void>;
  onRefuse: (p: Proposal, reason: string, against: string | null, note: string) => Promise<void>;
  onUndo: (d: Disposition) => Promise<void>;
  onDelete: (p: Proposal) => void;
  onSpan: (e: ProposalEvidence) => void;
  onGoto: (id: number) => void;
  onOpenRef: (ref: string) => void;
}

function Stand(props: StandProps) {
  const {
    proposal: p, position, disposition, neighbours, checked, twins, ctx, requirements, busy,
    mode, side, edits, editNote, reasonNote, writesRef, writesWhat,
    onSetMode, onSetSide, onSetEdits, onSetEditNote, onSetReasonNote,
    onHold, onRefuse, onUndo, onDelete, onSpan, onGoto, onOpenRef,
  } = props;

  const t = useT();
  const { tp, fmtDate } = useLocale();
  const dateShort = (iso: string | null | undefined): string => (iso ? fmtDate(String(iso).slice(0, 10)) : '—');
  const y = p.payload;
  const flags = flagsFor(p, neighbours, twins.map((x) => x.twin), checked, { t, tp });
  const isOpen = p.status === 'pending' && !disposition;
  const stmt = String(edits.statement ?? subjectOf(p));
  // A PROPOSAL is unheld wording, not the record — but §5.3 still applies: the payload text the
  // agent wrote can be read in the operator's content language, exactly like a held field. The
  // guard everywhere below is the same: once the operator has started an in-card amend (their own
  // draft sits in `edits`), that draft is what is shown, verbatim, never a translation of the
  // machine's original — correcting a translation must never be confused with amending the claim,
  // which is why <TxTools> is not offered here at all (the queue's own "Amend, then hold" already
  // owns editing this text).
  const amendingStatement = edits.statement !== undefined;
  const amendingName = edits.name !== undefined;
  const amendingTitle = edits.title !== undefined;
  // The generic branch (create_requirement / merge / create_deliverable_item) shows `stmt`, whose
  // source field varies by kind exactly as `subjectOf` picks it — mirror that order so the <Tx>
  // field name always matches what is actually on screen. `topic`/`question` are proposal fields
  // this lane was not asked to wire, so they render with no translation mark.
  const genericSubjectField: 'statement' | 'name' | 'title' | null =
    y.statement ? 'statement' : y.name ? 'name' : y.topic || y.question ? null : y.title ? 'title' : null;
  const top = neighbours?.[0] ?? null;
  const acParent = p.kind === 'create_ac' ? ctx?.ac_parents[String(y.requirement_ref ?? '')] ?? null : null;
  const winning = (side === 'a' ? y.position_a : y.position_b) ?? { statement: '', document_ref: '', quoted_span: '' };

  const field = (key: string, fallback: unknown = '') => String(edits[key] ?? y[key] ?? fallback);
  const set = (patch: Record<string, unknown>) => onSetEdits({ ...edits, ...patch });

  // ── what would enter the record ──────────────────────────────────────────────────────────
  const conflictSide = (raw: unknown, key: 'a' | 'b', label: string) => {
    const s = (raw ?? {}) as Record<string, unknown>;
    const meta = ctx?.docs[String(s.document_ref ?? '')];
    return (
      <div className={`pq-side${mode === 'resolve' && side === key ? ' win' : ''}`}>
        <div className="pq-sidehd">
          <span className="u-label">{label}</span>
          <span className="voice-figure">{s.effective_date ? dateShort(String(s.effective_date)) : ''}</span>
        </div>
        <p className="voice-record" style={{ fontSize: 'var(--t-scan)' }}>{String(s.statement ?? '')}</p>
        <blockquote className="ev" style={{ margin: '10px 0 0', fontSize: 'var(--t-scan)' }}>
          {String(s.quoted_span ?? '')}
        </blockquote>
        <div className="ev-src">
          <span className="att att--quoted" aria-hidden="true" />
          <span className="src-ref">{String(s.document_ref ?? '?')}</span>
          {meta?.effective_date ? (<><span className="sep">·</span><span className="voice-figure">{fmtDate(meta.effective_date)}</span></>) : null}
          {s.speaker ? (<><span className="sep">·</span><span>{String(s.speaker)}</span></>) : null}
        </div>
        {mode === 'resolve' ? (
          <button
            className={`btn btn-sm${side === key ? ' btn-accent' : ''}`}
            style={{ marginTop: 11, width: '100%', justifyContent: 'center' }}
            onClick={() => onSetSide(key)}
          >
            {side === key ? t('this side wins') : t('let this side win')}
          </button>
        ) : null}
      </div>
    );
  };

  return (
    <>
      <div className="pq-sthd">
        <span
          className={`att ${disposition ? (disposition.verdict === 'refused' ? '' : 'att--held')
            : p.status === 'rejected' ? '' : p.status === 'pending' ? 'att--proposed' : 'att--held'}`}
          aria-hidden="true"
        />
        <span className="pq-stkind">{t(KIND_NOUN[p.kind] ?? p.kind)}</span>
        <span>
          {t('filed by')} <span className="src-ref">{p.run_id === null ? t('no run') : t('run {id}', { id: p.run_id })}</span>{t(' on ')}
          <span className="voice-figure">{dateShort(p.created_at)}</span>
        </span>
        {flags.map((f) => (
          <span
            key={f.key}
            className={`chip${f.tone === 'attend' ? ' chip--attend' : f.tone === 'broken' ? ' chip--broken' : ''}`}
          >
            {f.long}
          </span>
        ))}
        <span className="src-ref" style={{ marginLeft: 'auto' }}>#{p.id}</span>
      </div>

      <div className="pq-stbody">
        {/* 1 · what would enter the record */}
        {p.kind === 'conflict' ? (
          <div className="pq-sec">
            <div className="pq-lbl">
              {t('The two things the record says')}
              {y.position_b?.effective_date ? (
                <span className="r">{t('the later document is {ref}, {date}', { ref: y.position_b.document_ref, date: dateShort(y.position_b.effective_date) })}</span>
              ) : null}
            </div>
            <div className="pq-sides">
              {conflictSide(y.position_a, 'a', t('Position A'))}
              {conflictSide(y.position_b, 'b', t('Position B'))}
            </div>
            {y.hinges_on ? <p className="pq-rat" style={{ marginTop: 12 }}>{t('Hinges on {x}.', { x: String(y.hinges_on) })}</p> : null}
            {String(y.anchor_matched_by ?? '') === 'auto' ? (
              <p className="pq-rat" style={{ marginTop: 8 }}>
                {t('The anchor was matched automatically — check it before resolving, because the resolution rewrites whatever it points at.')}
              </p>
            ) : null}
          </div>
        ) : isAmendmentKind(p.kind) ? (
          /* AN AMENDMENT names an existing row and changes SOME of its fields. It has no
             statement, so the generic branch rendered it as an em-dash with "nothing to compare
             it against" — the operator could see neither what it touched nor why. Show the ref,
             every field it would change, and the reason, which is the whole of what they judge. */
          <div className="pq-sec">
            <div className="pq-lbl">
              {t('Amends {ref}', { ref: amendmentRef(y as Record<string, unknown>) ?? '—' })}
              <span className="r">{amendedFields(y as Record<string, unknown>).map((f) => f.replace(/_/g, ' ')).join(' · ') || t('nothing')}</span>
            </div>
            {/* The values themselves — now vs becomes — are in the comparison panel below; this
               header carries only WHICH fields move and WHY, so the operator is not made to read
               the proposed description twice. */}
            {amendedFields(y as Record<string, unknown>).length === 0 ? (
              <p className="pq-rat">{t('This amendment names no field to change — refuse it.')}</p>
            ) : null}
            {y.reason ? (
              <p className="voice-record" style={{ marginTop: 10 }}>
                <b>{t('Why:')}</b> {String(y.reason)}
              </p>
            ) : null}
            <p className="pq-rat" style={{ marginTop: 8, opacity: .85 }}>
              {t('Only those fields change. Everything else on {ref} stays exactly as it is.', { ref: amendmentRef(y as Record<string, unknown>) ?? '—' })}
            </p>
          </div>
        ) : p.kind === 'create_deliverable' ? (
          <div className="pq-sec">
            <div className="pq-lbl">{t('Deliverable')}</div>
            {amendingName ? (
              <p className="voice-record" style={{ fontWeight: 600 }}>{field('name')}</p>
            ) : (
              <Tx type="proposal" id={p.id} field="payload.name" original={field('name')}
                as="p" className="voice-record" style={{ fontWeight: 600 }} />
            )}
            <div className="pq-lbl" style={{ marginTop: 16 }}>
              {t('Definition of done — the sentence acceptance is judged against')}
            </div>
            <p className="voice-record">{field('definition_of_done', '—')}</p>
            <div className="pq-attr">
              {(y.satisfies_refs ?? []).map((r) => (
                <span className="chip" key={r}><span className="src-ref">{r}</span></span>
              ))}
            </div>
            <p className="pq-rat" style={{ marginTop: 10 }}>
              {tp((y.satisfies_refs ?? []).length, '{n} requirement would be linked to it.', '{n} requirements would be linked to it.')}
            </p>
          </div>
        ) : p.kind === 'create_ac' ? (
          <div className="pq-sec">
            <div className="pq-lbl">
              {t('Acceptance criterion for')} <span className="src-ref">{String(y.requirement_ref ?? p.target_ref ?? '')}</span>
              {y.requirement_type ? <span className="r">{t(String(y.requirement_type))}</span> : null}
            </div>
            {amendingStatement ? (
              <p className="voice-record">{stmt}</p>
            ) : (
              <Tx type="proposal" id={p.id} field="payload.statement" original={stmt} as="p" className="voice-record" />
            )}
            <div className="pq-lbl" style={{ marginTop: 16 }}>{t('Measurement method')}</div>
            <p className="pq-rat">{field('measurement_method', '—')}</p>
          </div>
        ) : p.kind === 'create_test_case' ? (
          <div className="pq-sec">
            <div className="pq-lbl">
              {t('Test case')}
              <span className="r">{(y.ac_refs ?? []).join(' · ')}</span>
            </div>
            {amendingTitle ? (
              <p className="voice-record" style={{ fontWeight: 600 }}>{field('title')}</p>
            ) : (
              <Tx type="proposal" id={p.id} field="payload.title" original={field('title')}
                as="p" className="voice-record" style={{ fontWeight: 600 }} />
            )}
            {y.preconditions ? <p className="pq-rat" style={{ marginTop: 8 }}>{t('Preconditions:')} {field('preconditions')}</p> : null}
            <ol className="pq-rat" style={{ paddingLeft: 18, marginTop: 8 }}>
              {(y.steps ?? []).map((s) => (
                <li key={s.n}>{s.action}{s.expected ? <span> → {s.expected}</span> : null}</li>
              ))}
            </ol>
            <div className="pq-lbl" style={{ marginTop: 16 }}>{t('Expected result')}</div>
            <p className="voice-record">{field('expected_result', '—')}</p>
          </div>
        ) : p.kind === 'open_question' ? (
          <div className="pq-sec">
            <div className="pq-lbl">{t('The question that would go on the client-call agenda')}</div>
            <p className="voice-record">{field('question')}</p>
            {y.context ? <p className="pq-rat" style={{ marginTop: 8 }}>{field('context')}</p> : null}
          </div>
        ) : p.kind === 'update_requirement' ? (
          <div className="pq-sec">
            <div className="pq-lbl">
              {t('The revision that would enter {target}', { target: p.target_ref ?? t('the register') })}
              <span className="r">{t('the previous revision is kept, never overwritten')}</span>
            </div>
            <DiffLine before={String(y.previous_statement ?? p.target_statement ?? '')} after={stmt} />
            <div className="pq-attr">
              <span className="chip">{t('type {from} → {to}', { from: p.target_type ?? '—', to: field('type', '—') })}</span>
              <span className="chip">{t('priority {from} → {to}', { from: p.target_priority ?? '—', to: field('priority', '—') })}</span>
              {p.target_status ? <span className="chip">{t('now {status}', { status: p.target_status })}</span> : null}
            </div>
          </div>
        ) : (
          <div className="pq-sec">
            <div className="pq-lbl">{t('The statement that would enter the register')}</div>
            {amendingStatement || !genericSubjectField ? (
              <p className="voice-record">{stmt}</p>
            ) : (
              <Tx type="proposal" id={p.id} field={`payload.${genericSubjectField}`} original={stmt} as="p" className="voice-record" />
            )}
            <div className="pq-attr">
              <span className="chip">{field('type', '—')}</span>
              <span className="chip">{field('priority', '—')}</span>
              {field('category') ? <span className="chip">{field('category')}</span> : null}
            </div>
          </div>
        )}

        {/* 2 · the linter, ALWAYS in full — a warning under a passing lint is still a warning */}
        {y.lint ? (
          <div className="pq-sec">
            <div className="pq-lbl">
              {t('Linter')}
              <span className="r">{y.lint.pass ? t('no blocking error') : t('one error blocks a testable criterion')}</span>
            </div>
            {y.lint.flags.length ? (
              y.lint.flags.map((fl, i) => (
                <div key={i} className={`pq-lint ${fl.severity === 'error' ? 'err' : 'warn'}`}>
                  <span className="lm">{t(fl.severity)}</span>
                  <span>{fl.message}</span>
                </div>
              ))
            ) : (
              <div className="pq-lint pass">
                <span className="lm">{t('pass')}</span>
                <span>{t('Observable subject, observable outcome, a threshold a tester can check.')}</span>
              </div>
            )}
          </div>
        ) : null}

        {/* 3 · WHAT THE RECORD ALREADY SAYS */}
        <div className="pq-sec">
          <div className="pq-lbl">
            {t('What the record already says')}
            <span className="r">
              {!checked ? t('unavailable')
                : p.kind === 'conflict' ? t('the row this resolution rewrites')
                  : p.kind === 'create_ac' ? t('the requirement this criterion hangs from')
                    : TRIGRAM_KINDS.has(p.kind) && !neighbours ? t('not re-run for a disposed claim')
                      : isAmendmentKind(p.kind) ? t('the row this amendment changes')
                      : p.kind === 'create_deliverable' ? t('trigram over the deliverable names')
                        : TRIGRAM_KINDS.has(p.kind) ? t('trigram over the register · pg_trgm')
                          : t('nothing to compare it against')}
            </span>
          </div>

          {!checked ? (
            <div className="pq-nb">
              <div className="pq-nbhd">
                <span className="src-ref">{t('register comparison')}</span>
                <span className="sim">{t('did not answer')}</span>
              </div>
              <p className="pq-rat">
                {t('This claim has')} <b>{t('NOT')}</b>{' '}
                {t('been checked against the record. The page says so rather than showing an empty result that reads like a clean bill.')}
              </p>
            </div>
          ) : null}

          {isAmendmentKind(p.kind) ? (
            /* THE COMPARISON IS THE JUDGEMENT for an amendment. The panel used to say "nothing
               to compare it against", which was exactly backwards — this is the one kind where
               the record certainly exists. `current` is read live by the server at request time,
               not snapshotted when the proposal was filed, so a queue that sat for days shows
               what is true now rather than a stale before. */
            (() => {
              const cur = (p as unknown as { current?: Record<string, unknown> | null }).current ?? null;
              const ref = amendmentRef(y as Record<string, unknown>) ?? '—';
              if (!cur) {
                return (
                  <div className="pq-nb">
                    <div className="pq-nbhd"><span className="src-ref">{ref}</span></div>
                    <p className="pq-rat">{t('The record could not be read — hold this only after checking {ref} yourself.', { ref })}</p>
                  </div>
                );
              }
              if (cur.__missing) {
                return (
                  <div className="pq-nb hit">
                    <div className="pq-nbhd"><span className="src-ref">{ref}</span></div>
                    <p className="pq-rat">
                      {t('{ref} no longer exists — it was deleted after this was filed. Accepting will refuse; refuse it here instead.', { ref })}
                    </p>
                  </div>
                );
              }
              const changed = amendedFields(y as Record<string, unknown>);
              const arr = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : []);
              const show = (v: unknown): string =>
                (Array.isArray(v) ? (v.length ? v.join(' · ') : '—') : v === null || v === undefined || v === '' ? '—' : String(v));
              return (
                <div className="pq-nb">
                  <div className="pq-nbhd">
                    <span className="src-ref">{ref}</span>
                    <span>{show(cur.name ?? cur.title ?? cur.statement)}</span>
                  </div>
                  {changed.map((f) => {
                    const beforeVal = cur[f];
                    const afterVal = (y as Record<string, unknown>)[f];
                    // A list field (satisfies_refs, steps): show the SET delta — what this
                    // amendment adds and drops — not a word-diff that reads as noise.
                    if (Array.isArray(afterVal) || Array.isArray(beforeVal)) {
                      const b = new Set(arr(beforeVal));
                      const a = new Set(arr(afterVal));
                      const added = arr(afterVal).filter((x) => !b.has(x));
                      const removed = arr(beforeVal).filter((x) => !a.has(x));
                      return (
                        <div key={f} style={{ marginTop: 10 }}>
                          <div className="pq-lbl" style={{ opacity: .8 }}>{f.replace(/_/g, ' ')}</div>
                          {added.length === 0 && removed.length === 0 ? (
                            <p className="pq-rat">{t('Unchanged — the same set, re-sent.')}</p>
                          ) : (
                            <p className="pq-dif">
                              {added.map((x) => <ins key={'a' + x}>{x} </ins>)}
                              {removed.map((x) => <del key={'r' + x}>{x} </del>)}
                            </p>
                          )}
                        </div>
                      );
                    }
                    // Prose (name, description, statement …): two labelled blocks, never an inline
                    // word-diff. A description that was largely rewritten made the word-diff split
                    // mid-token ("…Verschlüsselung).Verschlüsselung);") — unreadable. Now / becomes
                    // is unambiguous and copy-pastes cleanly.
                    if (show(beforeVal) === show(afterVal)) {
                      return (
                        <div key={f} style={{ marginTop: 10 }}>
                          <div className="pq-lbl" style={{ opacity: .8 }}>{f.replace(/_/g, ' ')}</div>
                          <p className="pq-rat">{t('Unchanged — the same text, re-sent.')}</p>
                        </div>
                      );
                    }
                    return (
                      <div key={f} style={{ marginTop: 10 }}>
                        <div className="pq-lbl" style={{ opacity: .8 }}>{f.replace(/_/g, ' ')}</div>
                        <div className="pq-ba">
                          <span className="pq-ba-k">{t('now')}</span>
                          <p className="voice-record pq-ba-was" style={{ whiteSpace: 'pre-wrap' }}>{show(beforeVal)}</p>
                        </div>
                        <div className="pq-ba">
                          <span className="pq-ba-k">{t('becomes')}</span>
                          <p className="voice-record" style={{ whiteSpace: 'pre-wrap' }}>{show(afterVal)}</p>
                        </div>
                      </div>
                    );
                  })}
                  <p className="pq-rat" style={{ marginTop: 8, fontSize: 'var(--t-meta)' }}>
                    {t('Read live from the register just now — not a copy taken when the proposal was filed.')}
                  </p>
                </div>
              );
            })()
          ) : null}

          {acParent ? (
            <div className="pq-nb">
              <div className="pq-nbhd">
                <span className="src-ref">{acParent.ref}</span>
                <span>{t(REQUIREMENT_STATUS_LABEL[acParent.status]?.word ?? acParent.status)} · {t(acParent.priority)} · {t(acParent.type)}</span>
                <span className="sim">
                  {acParent.ac_count ? tp(acParent.ac_count, '{n} criterion already', '{n} criteria already') : t('no criteria yet')}
                </span>
              </div>
              <p className="voice-record">{acParent.statement}</p>
              <p className="pq-rat" style={{ marginTop: 8, fontSize: 'var(--t-meta)' }}>
                {t('A criterion is measured against its own requirement and its sibling criteria — never against the register at large, where the parent always scores highest and tells you nothing.')}
              </p>
              <div className="pq-nbacts">
                <button className="btn btn-sm btn-ghost" onClick={() => onOpenRef(acParent.ref)}>{t('Open {ref} →', { ref: acParent.ref })}</button>
              </div>
            </div>
          ) : null}

          {p.kind === 'conflict' && p.target_ref ? (
            <div className="pq-nb hit">
              <div className="pq-nbhd">
                <span className="src-ref">{p.target_ref}</span>
                <span>{p.target_status ?? ''}{p.target_type ? ` · ${p.target_type}` : ''}</span>
                <span className="sim">
                  {String(y.anchor_matched_by ?? '') === 'auto' ? t('anchor matched automatically') : t('anchored by the operator')}
                </span>
              </div>
              <p className="voice-record">{p.target_statement ?? ''}</p>
              {mode === 'resolve' ? (
                <>
                  <DiffLine before={p.target_statement ?? ''} after={String(winning.statement ?? '')} />
                  <p className="pq-rat" style={{ marginTop: 8, fontSize: 'var(--t-meta)' }}>
                    {t('what {ref} would say once position {side} wins', { ref: p.target_ref, side: side.toUpperCase() })}
                  </p>
                </>
              ) : null}
            </div>
          ) : null}

          {twins.map(({ twin, row }) => (
            <div className="pq-nb hit" key={twin.id}>
              <div className="pq-nbhd">
                <span className="src-ref">#{twin.id}</span>
                <span>
                  {t('filed by the same run')}
                  {row ? t(
                    new Date(row.created_at) > new Date(p.created_at) ? ', {s} s later' : ', {s} s earlier',
                    { s: Math.abs(Math.round((new Date(row.created_at).getTime() - new Date(p.created_at).getTime()) / 1000)) },
                  ) : ''}
                </span>
                <span className="sim">{t('similarity')} <span className="voice-figure">{twin.sim.toFixed(2)}</span></span>
              </div>
              {row ? <p className="voice-record">{subjectOf(row)}</p> : null}
              {row ? <DiffLine before={subjectOf(row)} after={stmt} /> : null}
              {isOpen ? (
                <div className="pq-nbacts">
                  <button
                    className="btn btn-sm"
                    disabled={busy}
                    onClick={() => void onRefuse(p, 'duplicate', `#${twin.id}`, 'the run said it twice')}
                  >
                    {t('Refuse — the run said it twice')}<span className="pq-kbd">d</span>
                  </button>
                  <button className="btn btn-sm btn-ghost" onClick={() => onGoto(twin.id)}>{t('Stand on #{id} →', { id: twin.id })}</button>
                </div>
              ) : null}
              <p className="pq-rat" style={{ marginTop: 8, fontSize: 'var(--t-meta)' }}>
                {t('Two claims one run filed minutes apart can both be held, and were: a register reference was created and deleted fifteen minutes later — burned for good, because references are never reused.')}
              </p>
            </div>
          ))}

          {checked && neighbours && TRIGRAM_KINDS.has(p.kind) ? (
            neighbours.length ? (
              <>
                {(() => {
                  const lead = neighbours[0];
                  const tail = neighbours.slice(1);
                  const isHit = lead.sim >= SIM_HIT;
                  return (
                    <>
                      <div className={`pq-nb${isHit ? ' hit' : ''}`}>
                        <div className="pq-nbhd">
                          <span className="src-ref">{lead.ref}</span>
                          <span>
                            {/* a neighbour is a requirement OR a deliverable, and only the
                                former has the register's status vocabulary — a deliverable's
                                'planned'/'delivered' must not be run through it */}
                            {t(lead.of === 'requirement'
                              ? (REQUIREMENT_STATUS_LABEL[lead.status]?.word ?? lead.status)
                              : lead.status)}
                            {lead.priority ? ` · ${t(lead.priority)}` : ''}
                            {lead.type ? ` · ${t(lead.type)}` : ''}
                          </span>
                          <span className="sim">
                            {t('similarity')} <span className="voice-figure">{lead.sim.toFixed(2)}</span>
                            {isHit ? '' : ` ${t('— under the 0.60 line')}`}
                          </span>
                        </div>
                        <p className="voice-record">{lead.statement}</p>
                        {lead.dod ? <p className="pq-rat" style={{ marginTop: 6, fontSize: 'var(--t-meta)' }}>{lead.dod}</p> : null}
                        {isHit ? (
                          <>
                            {lead.of === 'deliverable' ? (
                              <>
                                <p className="pq-rat" style={{ marginTop: 9, fontSize: 'var(--t-meta)' }}>
                                  {t('The names all but match. What differs is the sentence acceptance is judged against:')}
                                </p>
                                <DiffLine before={lead.dod ?? ''} after={String(y.definition_of_done ?? '')} />
                              </>
                            ) : (
                              <DiffLine before={lead.statement} after={stmt} />
                            )}
                            {isOpen ? (
                              <div className="pq-nbacts">
                                <button
                                  className="btn btn-sm"
                                  disabled={busy}
                                  onClick={() => void onRefuse(p, 'duplicate', lead.ref, '')}
                                >
                                  {t('Refuse — duplicate of {ref}', { ref: lead.ref })}<span className="pq-kbd">d</span>
                                </button>
                                {lead.of === 'requirement' ? (
                                  <button
                                    className="btn btn-sm"
                                    disabled={busy}
                                    onClick={() => void onRefuse(p, 'contradicts_register', lead.ref, '')}
                                  >
                                    {t('Refuse — contradicts {ref}', { ref: lead.ref })}
                                  </button>
                                ) : null}
                                <button className="btn btn-sm btn-ghost" onClick={() => onOpenRef(lead.ref)}>
                                  {t('Open {ref} →', { ref: lead.ref })}
                                </button>
                              </div>
                            ) : null}
                          </>
                        ) : null}
                      </div>
                      {tail.length ? (
                        <p className="pq-rat" style={{ fontSize: 'var(--t-meta)' }}>
                          {t('next closest')}{' '}
                          {tail.map((n) => (
                            <span key={n.ref}>
                              <span className="src-ref">{n.ref}</span>{' '}
                              <span className="voice-figure">{n.sim.toFixed(2)}</span>{' '}
                            </span>
                          ))}
                          {t('— nothing near')}
                        </p>
                      ) : null}
                    </>
                  );
                })()}
              </>
            ) : (
              <div className="pq-nbnone">
                {t('Nothing in the register scores above')} <span className="voice-figure">{SIM_FLOOR.toFixed(2)}</span>{' '}
                {t('— this claim is new ground. The trigram catches lexical near-twins and misses semantic ones, so this is a clean filter, not a clean bill.')}
              </div>
            )
          ) : checked && !neighbours && TRIGRAM_KINDS.has(p.kind) && !isOpen ? (
            <div className="pq-nbnone">
              {t('Disposed — the register comparison runs for standing claims only, and is not re-run here.')}
            </div>
          ) : null}
        </div>

        {/* 4 · evidence, in full dress, with the age of the document behind it */}
        {p.kind !== 'conflict' ? (
          <div className="pq-sec">
            <div className="pq-lbl">
              {t('Evidence')}
              <span className="r">{p.evidence.length ? tp(p.evidence.length, '{n} span', '{n} spans') : ''}</span>
            </div>
            {p.evidence.length ? (
              p.evidence.map((e, i) => {
                const meta = ctx?.docs[e.document_ref];
                return (
                  <div key={i} style={{ marginBottom: 12 }}>
                    <blockquote className="ev" style={{ margin: 0 }}>{e.quoted_span}</blockquote>
                    <div className="ev-src">
                      <span className="att att--quoted" aria-hidden="true" />
                      <span className="src-ref">{e.document_ref}</span>
                      {meta?.original_filename ? (<><span className="sep">·</span><span>{meta.original_filename}</span></>) : null}
                      {/* DESIGN §6 — effective_date is the date the CONTENT was true. On a
                          collision it is the deciding fact, so it stands beside the ref. */}
                      {meta?.effective_date ? (<><span className="sep">·</span><span className="voice-figure">{fmtDate(meta.effective_date)}</span></>) : null}
                      {e.locator?.speaker ? (<><span className="sep">·</span><span>{String(e.locator.speaker)}</span></>) : null}
                      {typeof e.confidence === 'number' ? (<><span className="sep">·</span><span className="voice-figure">{t('conf {n}', { n: e.confidence.toFixed(2) })}</span></>) : null}
                      <button className="btn btn-sm btn-ghost ml-auto" onClick={() => onSpan(e)}>
                        {t('view span')}<span className="pq-kbd">v</span>
                      </button>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="pq-rat">
                {t('None attached —')} {p.kind === 'create_deliverable'
                  ? t('a deliverable is derived from the requirements it claims to satisfy, not from a document. Judge it against the register above.')
                  : p.kind === 'create_ac'
                    ? t('a criterion is derived from its requirement, not from a document. It inherits {ref}’s evidence.', { ref: y.requirement_ref ? String(y.requirement_ref) : t('its parent') })
                    : t('this claim rests on nothing you can check. That alone is a reason to refuse it.')}
              </p>
            )}
          </div>
        ) : null}

        {/* 5 · the agent's reasoning — the SYSTEM speaking, kept quiet */}
        {y.rationale ? (
          <div className="pq-sec">
            <div className="pq-lbl">{t('Why the agent proposed it')}</div>
            <p className="pq-rat">{String(y.rationale)}</p>
          </div>
        ) : null}

        {p.reviewer_note ? (
          <div className="pq-sec">
            <div className="pq-lbl">{t('Reviewer note')}</div>
            <p className="pq-rat">{p.reviewer_note}</p>
          </div>
        ) : null}

        {/* the write preview — or, once disposed, what actually happened */}
        <div className="pq-sec">
          {disposition ? (
            <div className={`pq-wpre ${disposition.verdict === 'refused' ? 'pq-wpre--refused' : 'pq-wpre--held'}`}>
              <span className="u-label">{disposition.verdict === 'refused' ? t('Refused') : t('Held')}</span>
              <span>
                {disposition.verdict === 'refused' ? (
                  <>
                    {t('no register row was written. Reason recorded:')}{' '}
                    <b>{t(REJECT_REASON_LABEL[disposition.reason ?? ''] ?? disposition.reason ?? '')}</b>
                    {disposition.against ? ` — ${disposition.against}` : ''}
                  </>
                ) : (
                  <>
                    {t('written to the register as')} <span className="src-ref">{disposition.ref ?? '—'}</span>
                    {disposition.verdict === 'amended' ? ` ${t('· amended before holding')}` : ''}
                  </>
                )}
              </span>
              <button className="btn btn-sm btn-ghost ml-auto" disabled={busy} onClick={() => void onUndo(disposition)}>
                {t('Undo')}<span className="pq-kbd">u</span>
              </button>
            </div>
          ) : p.status !== 'pending' ? (
            <div className="pq-wpre pq-wpre--held">
              <span className="u-label">{t(dispositionWord(p.status, null))}</span>
              <span>
                {p.reviewed_by_name ? `${p.reviewed_by_name} · ` : ''}
                {p.reviewed_at ? dateShort(p.reviewed_at) : ''}
                {p.reject_reason ? ` · ${t(REJECT_REASON_LABEL[p.reject_reason] ?? p.reject_reason)}` : ''}
              </span>
              {/* The same undo the session ledger offers, for a disposition made in an EARLIER session:
                  a held create_* is reverted (the row it created is deleted — refused if something
                  already depends on it), a refusal is walked back whole. Either way the claim stands
                  UNTREATED again and the rail follows it there. */}
              {p.status === 'rejected' || REVERTIBLE_KINDS.has(p.kind) ? (
                <button
                  className="btn btn-sm btn-ghost ml-auto"
                  disabled={busy}
                  title={p.status === 'rejected'
                    ? t('Walk the refusal back — the claim stands unheld again')
                    : t('Revert the hold — deletes the row it created and puts the claim back in the queue')}
                  onClick={() => void onUndo({ proposal: p, verdict: p.status === 'rejected' ? 'refused' : 'held', ref: null, reason: null, against: null, at: 0 })}
                >
                  {p.status === 'rejected' ? t('Un-refuse — back to the queue') : t('Un-hold — back to the queue')}
                </button>
              ) : null}
            </div>
          ) : (
            <div className="pq-wpre">
              <span className="u-label">{t('Holding this writes')}</span>
              <span>
                <span className="src-ref">{writesRef}</span> — {writesWhat}{t('. Nothing else moves.')}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ── the action row: the FOOT of the sheet ─────────────────────────── */}
      {isOpen ? (
        <div className="pq-foot">
          <div className="pq-acts">
            <button
              className="btn btn-accent"
              disabled={busy}
              onClick={() => {
                if (p.kind === 'conflict' && mode !== 'resolve') { onSetMode('resolve'); onSetSide('b'); }
                else if (p.kind === 'conflict') {
                  void onHold(p, {
                    edits: {
                      apply_side: side,
                      ...(edits.target_requirement_id ? { target_requirement_id: Number(edits.target_requirement_id) } : {}),
                      ...(edits.statement ? { statement: String(edits.statement) } : {}),
                    },
                    note: editNote,
                  });
                } else void onHold(p);
              }}
            >
              {p.kind === 'conflict' ? (mode === 'resolve' ? t('Hold — position {side} wins', { side: side.toUpperCase() }) : t('Hold it — pick a side')) : t('Hold it')}
              <span className="pq-kbd">a</span>
            </button>
            {p.kind === 'conflict' ? (
              <button
                className="btn"
                disabled={busy}
                title={t('No side wins yet: both positions land as an open question on the Register page — your client-call agenda')}
                onClick={() => void onHold(p)}
              >
                {t('Log it as an open question')}
              </button>
            ) : (
              <button className="btn" disabled={busy} onClick={() => onSetMode(mode === 'amend' ? null : 'amend')}>
                {t('Amend, then hold')}<span className="pq-kbd">e</span>
              </button>
            )}
            {DISCUSS_KINDS.includes(p.kind) ? (
              <button
                className="btn"
                disabled={busy}
                title={t('Hold exactly as proposed AND put it on the client-call agenda as an open question to refine')}
                onClick={() => void onHold(p, { discuss: true })}
              >
                {t('Hold · and put to the client')}<span className="pq-kbd">c</span>
              </button>
            ) : null}
            <button className="btn btn-ghost" disabled={busy} onClick={() => onSetMode(mode === 'reasons' ? null : 'reasons')}>
              {t('Refuse')}<span className="pq-kbd">r</span>
            </button>
            <button
              type="button"
              className="reg-act reg-act--quiet"
              disabled={busy}
              title={t('Remove this machine proposal outright — no refusal record, the audit entry keeps its payload. A claim worth an answer is refused instead.')}
              onClick={() => onDelete(p)}
            >
              {t('Delete…')}
            </button>
            <span className="spacer" />
            <span className="pq-count">{position}</span>
          </div>

          {/* conflict resolution, in place — the 39-entry <select> is now the escape hatch */}
          {mode === 'resolve' ? (
            <div className="pq-edit">
              <div className="u-label" style={{ color: 'var(--ink-2)', marginBottom: 8 }}>
                {t('Position {side} wins. It is applied as a NEW REVISION of the anchored requirement — revisions are append-only, so the losing position stays preserved on this proposal.', { side: side.toUpperCase() })}
              </div>
              <div className="pq-edit-row">
                <select
                  value={String(edits.target_requirement_id ?? (p.target_requirement_id ?? ''))}
                  onChange={(e) => set({ target_requirement_id: e.target.value })}
                >
                  <option value="">{t('— none fits: create a NEW requirement from the winning position')}</option>
                  {requirements.map((r) => (
                    <option key={r.id} value={r.id}>{r.ref} — {(r.statement ?? '').slice(0, 70)}</option>
                  ))}
                </select>
              </div>
              <div style={{ marginTop: 9 }}>
                <textarea
                  rows={3}
                  placeholder={t('statement override (optional) — leave empty to take the winning side verbatim')}
                  value={String(edits.statement ?? '')}
                  onChange={(e) => set({ statement: e.target.value })}
                />
              </div>
              <div className="pq-edit-row">
                <input
                  placeholder={t('why you resolved it this way (recorded on the proposal)')}
                  value={editNote}
                  onChange={(e) => onSetEditNote(e.target.value)}
                />
              </div>
            </div>
          ) : null}

          {/* the in-card amend — the edit modal's per-kind field set, inside the sheet */}
          {mode === 'amend' ? (
            <div className="pq-edit">
              <div className="u-label" style={{ color: 'var(--ink-2)', marginBottom: 8 }}>
                {t('Amend, then hold — the amendment is recorded as')} <em>{t('held with edits')}</em>{t(', and the distance between the two is what the run report learns from')}
              </div>
              {p.kind === 'create_deliverable' ? (
                <>
                  <div className="pq-edit-row"><input value={field('name')} onChange={(e) => set({ name: e.target.value })} placeholder={t('name')} /></div>
                  <div style={{ marginTop: 9 }}>
                    <textarea rows={3} value={field('definition_of_done')} onChange={(e) => set({ definition_of_done: e.target.value })} />
                  </div>
                  <div className="pq-edit-row">
                    <input value={field('description')} onChange={(e) => set({ description: e.target.value })} placeholder={t('description')} />
                  </div>
                </>
              ) : p.kind === 'create_test_case' ? (
                <>
                  <div className="pq-edit-row"><input value={field('title')} onChange={(e) => set({ title: e.target.value })} placeholder={t('title')} /></div>
                  <div style={{ marginTop: 9 }}>
                    <textarea rows={2} value={field('expected_result')} onChange={(e) => set({ expected_result: e.target.value })} placeholder={t('expected result')} />
                  </div>
                  <div className="pq-edit-row">
                    <input value={field('preconditions')} onChange={(e) => set({ preconditions: e.target.value })} placeholder={t('preconditions')} />
                    <input value={field('test_data_ref')} onChange={(e) => set({ test_data_ref: e.target.value })} placeholder={t('test data reference')} />
                  </div>
                </>
              ) : p.kind === 'open_question' ? (
                <>
                  <textarea rows={3} value={field('question')} onChange={(e) => set({ question: e.target.value })} />
                  <div className="pq-edit-row">
                    <input value={field('context')} onChange={(e) => set({ context: e.target.value })} placeholder={t('context')} />
                  </div>
                </>
              ) : isAmendmentKind(p.kind) ? (
                /* Edit the PROPOSED values, field by field — never the "ref · fields" label the
                   generic branch below would have shown. A list field (satisfies_refs, steps) is
                   read-only here: hold the agent's set or refuse it, then re-map on the record. */
                <>
                  {amendedFields(y as Record<string, unknown>).map((f) => {
                    const val = (y as Record<string, unknown>)[f];
                    if (Array.isArray(val)) {
                      return (
                        <div key={f} className="pq-edit-row" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                          <span className="pq-lbl" style={{ opacity: .8 }}>{f.replace(/_/g, ' ')}</span>
                          <p className="voice-record" style={{ margin: '4px 0 0' }}>{val.map(String).join(' · ') || '—'}</p>
                          <span className="pq-rat">{t('Held as proposed — a list is not edited here.')}</span>
                        </div>
                      );
                    }
                    const long = f === 'description' || f === 'definition_of_done' || f === 'spec' || f === 'expected_result';
                    return (
                      <div key={f} className="pq-edit-row" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                        <span className="pq-lbl" style={{ opacity: .8 }}>{f.replace(/_/g, ' ')}</span>
                        {long ? (
                          <textarea rows={4} value={field(f)} onChange={(e) => set({ [f]: e.target.value })} />
                        ) : (
                          <input value={field(f)} onChange={(e) => set({ [f]: e.target.value })} />
                        )}
                      </div>
                    );
                  })}
                </>
              ) : (
                <>
                  <textarea rows={4} value={stmt} onChange={(e) => set({ statement: e.target.value })} />
                  {p.kind === 'create_ac' ? (
                    <div className="pq-edit-row">
                      <input
                        value={field('measurement_method')}
                        onChange={(e) => set({ measurement_method: e.target.value })}
                        placeholder={t('measurement method')}
                      />
                    </div>
                  ) : (
                    <>
                      <div className="pq-edit-row">
                        <select value={field('type', 'functional')} onChange={(e) => set({ type: e.target.value })}>
                          {TYPES.map((v) => <option key={v} value={v}>{t(v)}</option>)}
                        </select>
                        <select value={field('priority', 'should')} onChange={(e) => set({ priority: e.target.value })}>
                          {PRIORITIES.map((v) => <option key={v} value={v}>{t(v)}</option>)}
                        </select>
                        <input value={field('category')} onChange={(e) => set({ category: e.target.value })} placeholder={t('category')} />
                      </div>
                      <div className="pq-edit-row">
                        <input value={field('rationale')} onChange={(e) => set({ rationale: e.target.value })} placeholder={t('rationale')} />
                      </div>
                    </>
                  )}
                </>
              )}
              <div className="pq-edit-row">
                <input
                  value={editNote}
                  onChange={(e) => onSetEditNote(e.target.value)}
                  placeholder={t('why you changed it (recorded on the proposal)')}
                />
              </div>
              <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                <button
                  className="btn btn-accent"
                  disabled={busy}
                  onClick={() => void onHold(p, { edits, note: editNote })}
                >
                  {t('Hold the amended text')}
                </button>
                <button className="btn btn-ghost" onClick={() => { onSetMode(null); onSetEdits({}); }}>{t('Cancel')}</button>
              </div>
            </div>
          ) : null}

          {/* the refusal reasons — the ONLY signal the run report ever gets back */}
          {mode === 'reasons' ? (
            <div className="pq-reasons">
              <div className="u-label" style={{ color: 'var(--ink-2)' }}>
                {t('Refused because — the reason is the only signal the run report ever gets back')}
              </div>
              <div className="pq-rg">
                {PROPOSAL_REJECT_REASONS.map((r) => {
                  // The reason is pre-addressed only where the collision can carry it: a
                  // deliverable name that matches is a DUPLICATE, never a contradiction of the
                  // register — that verdict belongs to a requirement row.
                  const contextual = top && top.sim >= SIM_HIT
                    && (r.id === 'duplicate' || (r.id === 'contradicts_register' && top.of === 'requirement'));
                  const label = !contextual ? t(r.label)
                    : r.id === 'duplicate' ? t('Duplicate of {ref}', { ref: top!.ref }) : t('Contradicts {ref}', { ref: top!.ref });
                  return (
                    <button
                      key={r.id}
                      className={contextual ? 'pre' : ''}
                      disabled={busy}
                      onClick={() => void onRefuse(p, r.id, contextual ? top!.ref : null, reasonNote)}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              <div className="pq-edit-row" style={{ marginTop: 10 }}>
                <input
                  value={reasonNote}
                  onChange={(e) => onSetReasonNote(e.target.value)}
                  placeholder={t('note (optional) — recorded on the proposal beside the reason')}
                />
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
